<template>
  <div class="space-y-6">
    <!-- En-tête avec onglets -->
    <div class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
      <div>
        <h2 class="text-2xl font-extrabold text-blue-900 dark:text-gray-100">
          {{ isArchiveView ? 'Archives des Tickets' : 'Gestion des Tickets' }}
        </h2>
        <p class="text-gray-600 dark:text-gray-400 mt-1">
          {{ isArchiveView ? 'Tickets archivés - Consultation seule' : 'Tickets assignés à votre domaine' }}
        </p>
      </div>

      <!-- Onglets Archives/Actifs -->
      <div class="flex space-x-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
        <button 
          v-if="!isArchiveView && isDelegue"
          @click="openAddTicketModal"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center space-x-2 shadow-md"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          <span>Ajouter un Ticket</span>
        </button>
        <button 
          @click="switchToActiveTickets"
          :class="[
            'px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
            !isArchiveView 
              ? 'bg-white text-blue-900 shadow-sm dark:bg-gray-600 dark:text-white' 
              : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
          ]"
        >
          Tickets Actifs
        </button>
        <button 
          @click="switchToArchives"
          :class="[
            'px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
            isArchiveView 
              ? 'bg-white text-blue-900 shadow-sm dark:bg-gray-600 dark:text-white' 
              : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
          ]"
        >
          Archives
        </button>
      </div>
    </div>

    <!-- Indicateur de filtre par ticket -->
    <div v-if="filteredByTicketCode" class="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <div class="h-8 w-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
            <svg class="h-4 w-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-blue-900 dark:text-blue-100">
              Affichage du ticket spécifique: <strong>{{ selectedTicketCode }}</strong>
            </p>
            <p class="text-xs text-blue-600 dark:text-blue-300">
              Cliqué depuis les notifications - {{ filteredTickets.length }} résultat(s)
            </p>
          </div>
        </div>
        <button 
          @click="clearTicketFilter"
          class="flex items-center space-x-1 px-3 py-1 text-xs bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
        >
          <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
          <span>Afficher tous les tickets</span>
        </button>
      </div>
    </div>

    <!-- Filtres rapides et recherche LOCALE -->
    <div class="flex flex-wrap gap-2" v-if="!isArchiveView">
      <button 
          @click="exportSelectedTickets" 
          :disabled="selectedTickets.length === 0"
          :class="[
            'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-2',
            selectedTickets.length > 0
              ? 'bg-green-600 text-white hover:bg-green-700 shadow-md'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          ]"
        >
          <img 
            src="/images/exceller.png" 
            alt="Exporter Excel" 
            class="w-5 h-5 object-contain"
          >
          <span>({{ selectedTickets.length }})</span>
        </button>
      <button v-for="filter in statusFilters" :key="filter.value" @click="applyStatusFilter(filter.value)" :class="[
        'px-3 py-1 rounded-full text-sm font-medium transition-all duration-200 border',
        activeStatusFilter === filter.value
          ? 'bg-blue-900 text-white border-blue-900 shadow-md transform scale-105'
          : filter.color + ' hover:shadow-md hover:scale-105 dark:border-gray-600'
      ]">
        {{ filter.label }}
      </button>
    </div>

    <!-- Tableau des tickets -->
    <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      <div 
        class="overflow-x-auto table-container"
        ref="tableContainer"
        @mousedown="startDrag"
        @mousemove="doDrag"
        @mouseup="endDrag"
        @mouseleave="endDrag"
        @touchstart="startDrag"
        @touchmove="doDrag"
        @touchend="endDrag"
      >
        <table class="w-full">
          <thead class="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-gray-700 dark:to-gray-600">
            <tr>
              <!-- Colonne Sélection (uniquement pour les tickets actifs) -->
              <th class="px-4 py-4 text-center" v-if="!isArchiveView">
                <input 
                  type="checkbox" 
                  v-model="selectAllFiltered"
                  @change="toggleSelectAllFiltered"
                  class="h-4 w-4 text-blue-900 rounded border-gray-300 focus:ring-blue-900"
                >
              </th>
              
              <!-- EN-TÊTES DE COLONNES -->
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Numéro
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Date
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Client
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Code
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Statut
              </th>
              
              <!-- Colonne Archivé (uniquement pour les archives) -->
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider" v-if="isArchiveView">
                Archivé le
              </th>
              
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Fichier Joint
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Gamme
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Produit
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Motif
              </th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Responsable
              </th>
              <th class="px-6 py-4 text-right text-xs font-semibold text-black-700 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-600">
            <tr 
              v-for="ticket in paginatedTickets" 
              :key="ticket.id"
              :data-ticket-id="ticket.id"
              :class="[
                'transition-colors duration-150 group table-row cursor-pointer relative',
                ticket.status === 'resolu' 
                  ? 'hover:bg-green-100 dark:hover:bg-green-900/10' 
                  : 'hover:bg-blue-50 dark:hover:bg-gray-700/50',
                selectedRow === ticket.id ? 'selected-row bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : '',
                isArchiveView ? 'bg-gray-50 dark:bg-gray-700/30' : ''
              ]"
              @click="handleRowClick($event, ticket.id)"
              @mouseenter="handleRowHover(ticket)"
            >
              
              <!-- Colonne Sélection (uniquement pour les tickets actifs) -->
              <td class="px-4 py-3 whitespace-nowrap text-center relative" v-if="!isArchiveView">
                <input 
                  type="checkbox" 
                  :value="ticket.id"
                  v-model="selectedTickets"
                  @click.stop
                  class="h-4 w-4 text-blue-900 rounded border-gray-300 focus:ring-blue-900"
                >
              </td>

              <!-- Colonne Numéro -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div
                    :class="[
                      'flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold text-sm mr-3',
                      isArchiveView 
                        ? 'bg-gradient-to-br from-gray-500 to-gray-700' 
                        : 'bg-gradient-to-br from-purple-500 to-purple-700'
                    ]">
                    {{ getTicketCode(ticket.code) }}
                  </div>
                  <div>
                    <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">{{ ticket.code }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">Ticket</div>
                  </div>
                </div>
              </td>

              <!-- Colonne Date -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-500 dark:text-gray-400 font-medium">
                  <div>{{ formatDate(ticket.created_at) }}</div>
                  <!-- Affichage de la date de transfert si transféré -->
                  <div v-if="ticket.transferer && ticket.datetransferer" 
                        class="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    <div class="font-medium">date:</div>
                    <div>{{ formatDateTime(ticket.datetransferer) }}</div>
                  </div>
                </div>
              </td>

              <!-- Colonne Client -->
              <td class="px-6 py-4">
                <div>
                  <div class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ ticket.nom }}</div>
                  <div class="text-sm text-gray-500 dark:text-gray-400">{{ ticket.email }}</div>
                  <div class="text-xs text-gray-400 dark:text-gray-500">{{ ticket.telephone }}</div>
                </div>
              </td>

              <!-- Colonne Code Client -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-center">
                  <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {{ ticket.codeclient || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Code
                  </div>
                </div>
              </td>

              <!-- Colonne Statut -->
              <td class="px-6 py-4 whitespace-nowrap">
                <select 
                  v-model="ticket.status" 
                  @change="updateTicketStatus(ticket)" 
                  @click.stop 
                  :class="[
                    'px-3 py-1 text-xs font-medium rounded-full border focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all duration-200 cursor-pointer w-full min-w-[120px]',
                    getStatusBadgeClass(ticket.status)
                  ]"
                  :disabled="isArchiveView"
                  style="appearance: menulist; -webkit-appearance: menulist; -moz-appearance: menulist;"
                >
                  <option value="ouvert">Ouvert</option>
                  <option value="en_attente">En attente</option>
                  <option value="resolu">Résolu</option>
                  <option value="ferme">Fermé</option>
                </select>
              </td>

              <!-- Colonne Archivé (uniquement pour les archives) -->
              <td class="px-6 py-4 whitespace-nowrap" v-if="isArchiveView">
                <div class="text-center">
                  <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                    <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                    </svg>
                    Archivé
                  </span>
                  <div class="text-xs text-gray-500 dark:text-gray-400 mt-1" v-if="ticket.archived_at">
                    {{ formatDate(ticket.archived_at) }}
                  </div>
                </div>
              </td>

              <!-- Colonne Fichier Joint -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex flex-col space-y-1 max-w-xs">
                  <!-- État de chargement -->
                  <div v-if="ticket.loadingFichiers" class="text-center">
                    <div class="inline-flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                      <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span class="text-xs">Chargement...</span>
                    </div>
                  </div>

                  <!-- Liste des fichiers -->
                  <div v-else-if="ticket.fichiers && ticket.fichiers.length > 0" class="space-y-1">
                    <div v-for="fichier in ticket.fichiers.slice(0, 2)" :key="fichier.chemin" 
                         class="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 hover:shadow-sm transition-shadow duration-200 group">
                      <div class="flex items-center space-x-2 flex-1 min-w-0">
                        <span class="text-sm">{{ fichier.icone }}</span>
                        <div class="flex-1 min-w-0">
                          <!-- Nom de fichier tronqué -->
                          <div class="text-xs font-medium text-gray-900 dark:text-gray-100 truncate" :title="fichier.nom">
                            {{ truncateFileName(fichier.nom) }}
                          </div>
                          <div class="text-xs text-gray-500 dark:text-gray-400">
                            {{ fichier.taille }}
                          </div>
                        </div>
                      </div>
                      <div class="flex items-center space-x-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <a :href="fichier.url" 
                           target="_blank" 
                           class="p-1 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded transition-all duration-200"
                           :title="`Voir ${fichier.nom}`"
                           @click.stop>
                          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                        </a>
                        <a :href="fichier.url" 
                           download 
                           class="p-1 text-green-600 dark:text-green-400 hover:text-white hover:bg-green-600 dark:hover:bg-green-500 rounded transition-all duration-200"
                           :title="`Télécharger ${fichier.nom}`"
                           @click.stop>
                          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                        </a>
                      </div>
                    </div>
                    
                    <!-- Indicateur de fichiers supplémentaires -->
                    <div v-if="ticket.fichiers.length > 2" class="text-center">
                      <span class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                        +{{ ticket.fichiers.length - 2 }} autre(s)
                      </span>
                    </div>
                  </div>

                  <!-- Message aucun fichier -->
                  <div v-else class="text-center">
                    <div class="text-xs text-gray-400 dark:text-gray-500 italic">
                      Aucun fichier
                    </div>
                  </div>
                </div>
              </td>

              <!-- Colonne Gamme -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-gray-100">
                  <div>{{ ticket.relations?.gamme || ticket.gamme?.name || 'Non spécifiée' }}</div>
                  <!-- Affichage de l'ancienne gamme si transféré -->
                  <div v-if="ticket.transferer" 
                       class="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    <div class="font-medium">Ancienne gamme:</div>
                    <div>{{ ticket.relations?.gammeTransferer || ticket.ancienne_gamme_name || getGammeTransferName(ticket) }}</div>
                  </div>
                </div>
              </td>

              <!-- Colonne Produit -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900 dark:text-gray-100">
                  <div>{{ ticket.produit?.name || ticket.relations?.produit || 'Non spécifié' }}</div>
                  <!-- Affichage de l'ancien produit si transféré -->
                  <div v-if="ticket.transferer && ticket.produit_id_transferer" 
                       class="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    <div class="font-medium">Ancien produit:</div>
                    <div>{{ getProduitTransferName(ticket) }}</div>
                  </div>
                </div>
              </td>

              <!-- Colonne Motif -->
              <td class="px-6 py-4">
                <div class="text-sm text-gray-600 dark:text-gray-300 max-w-xs">
                  <div>{{ ticket.relations?.motif || ticket.motif?.name || 'Non spécifié' }}</div>
                  <!-- Affichage de l'ancien motif si transféré -->
                  <div v-if="ticket.transferer" 
                       class="text-xs text-orange-600 dark:text-orange-400 mt-1">
                    <div class="font-medium">Ancien motif:</div>
                    <div>{{ ticket.relations?.motifTransferer || ticket.ancien_motif_name || getMotifTransferName(ticket) }}</div>
                  </div>
                </div>
              </td>

              <!-- Colonne Responsable -->
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div v-if="ticket.responsable" class="flex items-center space-x-2">
                    <div class="h-8 w-8 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                      <svg class="h-4 w-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                    </div>
                    <div>
                      <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {{ ticket.responsable.name }}
                      </div>
                      <div class="text-xs text-gray-500 dark:text-gray-400">
                        {{ ticket.responsable.email }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-sm text-gray-400 dark:text-gray-500 italic">
                    Non assigné
                  </div>
                </div>
              </td>

              <!-- Colonne Actions -->
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div class="flex justify-end space-x-1 transition-opacity duration-200">
                  <!-- Bouton Transférer (uniquement pour les tickets actifs et admin) -->
                  <button v-if="!isArchiveView && isAdmin" @click.stop="openTransferModal(ticket)"
                    class="p-2 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200 group/btn"
                    title="Transférer le ticket">
                    <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                    </svg>
                  </button>

                  <!-- Bouton Voir détails -->
                  <button @click.stop="viewTicket(ticket)"
                    class="p-2 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200 group/btn"
                    title="Voir détails">
                    <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                      </path>
                    </svg>
                  </button>
                  
                  <!-- Bouton Supprimer (uniquement pour les tickets actifs et admin) -->
                  <button v-if="!isArchiveView && isAdmin" 
        @click.stop="archiverTicket(ticket)"
        class="p-2 text-orange-600 dark:text-orange-400 hover:text-white hover:bg-orange-600 dark:hover:bg-orange-500 rounded-lg transition-all duration-200 group/btn"
        title="Archiver le ticket">
  <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
  </svg>
</button>
                  
                  <!-- Bouton Désarchiver (uniquement pour les archives et admin) -->
                  <button v-if="isArchiveView && isAdmin" @click.stop="desarchiverTicket(ticket)"
                    class="p-2 text-green-600 dark:text-green-400 hover:text-white hover:bg-green-600 dark:hover:bg-green-500 rounded-lg transition-all duration-200 group/btn"
                    title="Désarchiver">
                    <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                    </svg>
                  </button>
                  
                  <!-- Bouton Ajouter une réponse (uniquement pour les tickets actifs) -->
                  <button v-if="!isArchiveView" @click.stop="openReponseModal(ticket)"
                    class="p-2 text-purple-600 dark:text-purple-400 hover:text-white hover:bg-purple-600 dark:hover:bg-purple-500 rounded-lg transition-all duration-200 group/btn"
                    title="Ajouter une réponse">
                    <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                      </path>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- BOUTONS FLOTTANTS UNIQUES (en dehors du tableau) -->
        <div 
          v-if="selectedRow && selectedTicketForFloating"
          class="floating-actions-modal"
          :style="getFloatingButtonsStyle()"
          @click.stop
        >
          <div class="flex flex-row items-center justify-center space-x-2 w-full">
            <!-- Bouton Transférer (uniquement pour les tickets actifs) -->
            <button v-if="!isArchiveView && isAdmin" @click.stop="openTransferModal(selectedTicketForFloating)" 
              class="flex-1 min-w-[40px] h-8 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm border border-blue-200 dark:border-blue-600"
              title="Transférer le ticket">
              <svg class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
              </svg>
            </button>

            <!-- Bouton Voir détails -->
            <button @click.stop="viewTicket(selectedTicketForFloating)" 
              class="flex-1 min-w-[40px] h-8 flex items-center justify-center text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm border border-blue-200 dark:border-blue-600"
              title="Voir détails">
              <svg class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
            </button>

            <!-- Bouton Supprimer (uniquement pour les tickets actifs) -->
            <!-- Dans la section des boutons flottants, remplacez le bouton Supprimer par : -->
<button v-if="!isArchiveView && isAdmin" 
        @click.stop="archiverTicket(selectedTicketForFloating)" 
        class="flex-1 min-w-[40px] h-8 flex items-center justify-center text-orange-600 dark:text-orange-400 hover:text-white hover:bg-orange-600 dark:hover:bg-orange-500 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm border border-orange-200 dark:border-orange-600"
        title="Archiver le ticket">
  <svg class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
  </svg>
</button>

            <!-- Bouton Désarchiver (uniquement pour les archives) -->
            <button v-if="isArchiveView && isAdmin" @click.stop="desarchiverTicket(selectedTicketForFloating)" 
              class="flex-1 min-w-[40px] h-8 flex items-center justify-center text-green-600 dark:text-green-400 hover:text-white hover:bg-green-600 dark:hover:bg-green-500 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm border border-green-200 dark:border-green-600"
              title="Désarchiver">
              <svg class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
              </svg>
            </button>

            <!-- Bouton Ajouter une réponse (uniquement pour les tickets actifs) -->
            <button v-if="!isArchiveView" @click.stop="openReponseModal(selectedTicketForFloating)" 
              class="flex-1 min-w-[40px] h-8 flex items-center justify-center text-purple-600 dark:text-purple-400 hover:text-white hover:bg-purple-600 dark:hover:bg-purple-500 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 bg-white/90 dark:bg-gray-700/90 backdrop-blur-sm border border-purple-200 dark:border-purple-600"
              title="Ajouter une réponse">
              <svg class="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- État de chargement -->
      <div v-if="loading" class="p-8 text-center">
        <div class="inline-flex items-center space-x-3">
          <svg class="animate-spin h-6 w-6 text-blue-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-gray-600 dark:text-gray-400 font-medium">
            {{ isArchiveView ? 'Chargement des archives...' : 'Chargement des tickets...' }}
          </span>
        </div>
      </div>

      <!-- Message vide -->
      <div v-if="!loading && filteredTickets.length === 0" class="p-12 text-center">
        <div class="max-w-sm mx-auto">
          <div :class="[
            'h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-4',
            isArchiveView 
              ? 'bg-gray-100 dark:bg-gray-600' 
              : 'bg-blue-100 dark:bg-blue-900/20'
          ]">
            <svg :class="[
              'h-10 w-10',
              isArchiveView 
                ? 'text-gray-400 dark:text-gray-500' 
                : 'text-blue-900 dark:text-blue-300'
            ]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {{ isArchiveView ? 'Aucun ticket archivé' : (filteredByTicketCode ? 'Ticket non trouvé' : 'Aucun ticket trouvé') }}
          </h3>
          <p class="text-gray-500 dark:text-gray-400">
            {{ isArchiveView 
              ? 'Aucun ticket n\'est actuellement archivé.' 
              : (filteredByTicketCode 
                ? `Le ticket "${selectedTicketCode}" n'a pas été trouvé dans la liste` 
                : 'Aucun ticket ne correspond à vos critères de recherche.') 
            }}
          </p>
          <button 
            v-if="filteredByTicketCode"
            @click="clearTicketFilter"
            class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Afficher tous les tickets
          </button>
        </div>
      </div>

      <!-- Pagination améliorée -->
      <div v-if="!loading && filteredTickets.length > 0" class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
            <span>Total:</span>
            <span class="font-semibold text-blue-900 dark:text-blue-300">{{ totalItems }}</span>
            <span>{{ isArchiveView ? 'tickets archivés' : 'tickets' }}</span>
            <span v-if="selectedTickets.length > 0 && !isArchiveView" class="ml-4 font-semibold text-green-600">
              ({{ selectedTickets.length }} sélectionné(s))
            </span>
          </div>

          <div class="flex items-center space-x-2">
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-700 dark:text-gray-300">Afficher :</span>
              <select 
                v-model="itemsPerPage" 
                @change="currentPage = 1"
                class="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-900"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>

            <div class="flex items-center space-x-1">
              <button
                @click="currentPage = 1"
                :disabled="currentPage === 1"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Première page"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                </svg>
              </button>
              
              <button
                @click="currentPage--"
                :disabled="currentPage === 1"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Page précédente"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>

              <div class="flex items-center space-x-1">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="currentPage = page"
                  :class="[
                    'px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200',
                    currentPage === page 
                      ? 'bg-blue-900 text-white shadow-sm' 
                      : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  ]"
                >
                  {{ page }}
                </button>
                
                <span v-if="hasMorePages" class="px-2 text-gray-500 dark:text-gray-400">...</span>
              </div>

              <button
                @click="currentPage++"
                :disabled="currentPage === totalPages"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Page suivante"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
              
              <button
                @click="currentPage = totalPages"
                :disabled="currentPage === totalPages"
                class="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Dernière page"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de détail du ticket -->
    <div v-if="showDetailModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm transition-all duration-300"
      @click.self="closeDetailModal">
      <div
        class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-95 border border-gray-200 dark:border-gray-600">
        <!-- En-tête de la modal -->
        <div class="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4 ">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                  </path>
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-white">
                  Détails du Ticket {{ selectedTicket?.code }}
                </h3>
                <p class="text-blue-100 text-sm">
                  Informations complètes sur la réclamation
                </p>
              </div>
            </div>
            <button @click="closeDetailModal"
              class="h-8 w-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200 group">
              <svg class="h-5 w-5 text-white group-hover:scale-110 transition-transform" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Contenu de la modal -->
        <div class="p-6">
          <div v-if="selectedTicket" class="space-y-6">
            <!-- Informations principales -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <!-- Informations client -->
              <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                  </svg>
                  Informations Client
                </h4>
                <div class="space-y-3">
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Nom:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedTicket.nom }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Email:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedTicket.email || 'Non renseigné'
                      }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Téléphone:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedTicket.telephone }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Wilaya:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedTicket.wilaya || 'Non renseigné'
                      }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Représentant:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedTicket.represprofert || 'Non renseigné' }}</span>
                  </div>
                </div>
              </div>

              <!-- Informations produit -->
              <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                  <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                  </svg>
                  Informations Produit
                </h4>
                <div class="space-y-3">
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Gamme:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedTicket.gamme?.name }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Motif:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedTicket.motif?.name }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">N° Lot:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedTicket.numerolot || 'Non renseigné' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Date livraison:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ formatDate(selectedTicket.datelivraison)
                      }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-sm font-medium text-gray-600 dark:text-gray-400">Quantité non conforme:</span>
                    <span class="text-sm text-gray-900 dark:text-gray-100">{{ selectedTicket.quantite || 'Non renseigné'
                      }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
              <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z">
                  </path>
                </svg>
                Description de la réclamation
              </h4>
              <div class="bg-white dark:bg-gray-600 rounded-lg p-4 border border-gray-200 dark:border-gray-500">
                <p class="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {{ selectedTicket.description || 'Aucune description fournie' }}
                </p>
              </div>
            </div>
            <!-- Dans la modal de détails du ticket -->
<div class="bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-xl p-4 border border-yellow-200 dark:border-yellow-700">
  <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
    <svg class="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
    </svg>
    Gestion des Rappels
  </h4>
  
  <!-- Affichage du rappel existant -->
  <div v-if="selectedTicket.dateRappel" class="mb-4">
    <div class="flex items-center justify-between p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg border border-yellow-200 dark:border-yellow-700">
      <div class="flex items-center space-x-3">
        <div class="h-10 w-10 bg-yellow-500 rounded-full flex items-center justify-center">
          <svg class="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div>
          <div class="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
            Rappel programmé
          </div>
          <div class="text-sm text-yellow-600 dark:text-yellow-300">
    {{ selectedTicket.dateRappel_formatee }}
</div>
        </div>
      </div>
      <button 
        v-if="!isArchiveView"
        @click="supprimerRappel"
        class="px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:text-white hover:bg-red-600 dark:hover:bg-red-500 rounded-lg transition-all duration-200 flex items-center space-x-1"
      >
        <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
        </svg>
        <span>Supprimer</span>
      </button>
    </div>
  </div>

  <!-- Formulaire pour définir un nouveau rappel -->
  <div v-else class="mb-4">
    <div class="space-y-3">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Définir un rappel
        </label>
        <div class="flex items-center space-x-2">
          <input 
            v-model="nombreJoursRappel"
            type="number"
            min="1"
            max="365"
            placeholder="Nombre de jours"
            class="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
          >
          <button 
            @click="definirRappel"
            :disabled="!nombreJoursRappel || definirRappelEnCours"
            :class="[
              'px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2',
              nombreJoursRappel && !definirRappelEnCours
                ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            ]"
          >
            <svg v-if="definirRappelEnCours" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Définir</span>
          </button>
        </div>
        <div v-if="dateRappelCalculee" class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Rappel programmé pour le : <strong>{{ dateRappelCalculee }}</strong>
        </div>
      </div>
<!--       
      <div class="grid grid-cols-2 gap-2">
        <button 
          v-for="jours in [1, 3, 7, 30]"
          :key="jours"
          @click="definirRappelRapide(jours)"
          class="px-3 py-2 text-sm border border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-800/50 transition-colors duration-200"
        >
          Dans {{ jours }} jour{{ jours > 1 ? 's' : '' }}
        </button>
      </div> -->
    </div>
  </div>

  <!-- Affichage des rappels à venir -->
  <div v-if="selectedTicket.id && !isArchiveView" class="mt-4">
    <button 
      @click="toggleRappelsList"
      class="flex items-center justify-between w-full p-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
    >
      <span class="flex items-center">
        <svg class="h-4 w-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
        </svg>
        Rappels à venir
      </span>
      <svg :class="['h-4 w-4 transition-transform duration-200', showRappelsList ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    </button>
    
    <div v-if="showRappelsList" class="mt-2 space-y-2 max-h-40 overflow-y-auto">
      <div v-if="loadingRappels" class="text-center py-2">
        <div class="inline-flex items-center space-x-2 text-gray-500 dark:text-gray-400">
          <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-xs">Chargement...</span>
        </div>
      </div>
      
      <div v-else-if="rappelsAujourdhui.length > 0" class="space-y-2">
        <div v-for="rappel in rappelsAujourdhui" :key="rappel.id" class="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <div class="flex items-center justify-between">
            <div>
              <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
                {{ rappel.code }}
              </div>
              <div class="text-xs text-gray-600 dark:text-gray-400">
                {{ rappel.nom }}
              </div>
            </div>
            <span class="text-xs px-2 py-1 bg-yellow-500 text-white rounded-full">
              Aujourd'hui
            </span>
          </div>
        </div>
      </div>
      
      <div v-else class="text-center py-2">
        <div class="text-xs text-gray-500 dark:text-gray-400 italic">
          Aucun rappel programmé pour aujourd'hui
        </div>
      </div>
    </div>
  </div>
</div>

            <!-- Statut et dates -->
            <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
              <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z">
                  </path>
                </svg>
                Statut du Ticket
              </h4>
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div class="flex items-center space-x-3">
                  <select v-model="selectedTicket.status" @change="updateTicketStatus(selectedTicket)" :class="[
                    'px-3 py-2 rounded-full text-sm font-medium border-0 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all duration-200 cursor-pointer',
                    getStatusBadgeClass(selectedTicket.status)
                  ]" :disabled="isArchiveView">
                    <option value="ouvert" :class="getStatusOptionClass('ouvert')">Ouvert</option>
                    <option value="en_attente" :class="getStatusOptionClass('en_attente')">En attente</option>
                    <option value="resolu" :class="getStatusOptionClass('resolu')">Résolu</option>
                    <option value="ferme" :class="getStatusOptionClass('ferme')">Fermé</option>
                  </select>
                  <div class="text-sm text-gray-500 dark:text-gray-400">
                    Créé le {{ formatDate(selectedTicket.created_at) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Section Historique -->
            <div class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
              <h4 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <svg class="h-5 w-5 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Historique du Ticket
              </h4>

              <!-- État de chargement -->
              <div v-if="loadingHistorique" class="text-center py-4">
                <div class="inline-flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                  <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                    </path>
                  </svg>
                  <span class="text-sm">Chargement de l'historique...</span>
                </div>
              </div>

              <!-- Liste de l'historique -->
              <div v-else-if="formattedHistorique.length > 0" class="space-y-3 max-h-60 overflow-y-auto">
                <div v-for="(item, index) in formattedHistorique" :key="item.id"
                  class="flex items-start space-x-3 p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 hover:shadow-sm transition-shadow duration-200">
                  <!-- Timeline dot -->
                  <div class="flex-shrink-0 mt-1">
                    <div class="h-3 w-3 rounded-full bg-blue-500 border-2 border-white dark:border-gray-600 shadow-sm">
                    </div>
                  </div>

                  <!-- Contenu -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center justify-between mb-1">
                      <span :class="['px-2 py-1 text-xs font-medium rounded-full', item.statusClass]">
                        {{ item.statusText }}
                      </span>
                      <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        {{ item.formattedDate }}
                      </span>
                    </div>
                    <div class="text-sm text-gray-600 dark:text-gray-300">
                      Statut du ticket modifié
                    </div>
                  </div>
                </div>
              </div>

              <!-- Message vide -->
              <div v-else class="text-center py-6">
                <div class="max-w-sm mx-auto">
                  <div
                    class="h-12 w-12 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg class="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Aucun historique</h5>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    Aucune modification enregistrée pour ce ticket
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de réponse -->
    <div v-if="showReponseModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm transition-all duration-300"
      @click.self="closeReponseModal">
      <div
        class="bg-white dark:bg-gray-800 shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-95 rounded-2xl border border-gray-200 dark:border-gray-600">
        
        <!-- En-tête de la modal -->
        <div class="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                  </path>
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-white">
                  Détails du Ticket & Réponses
                </h3>
                <p class="text-blue-100 text-sm">
                  {{ selectedTicketForReponse?.code }} - {{ selectedTicketForReponse?.nom }}
                </p>
              </div>
            </div>
            <button @click="closeReponseModal($event)"
              class="h-8 w-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200 group">
              <svg class="h-5 w-5 text-white group-hover:scale-110 transition-transform" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Contenu de la modal -->
        <div class="p-6">
          <div v-if="selectedTicketForReponse" class="space-y-6">
            
            <!-- Section Informations du Ticket - COMPACTE -->
            <div class="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
              <!-- En-tête compact -->
              <div class="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-600 dark:to-gray-700 px-4 py-3 border-b border-gray-200 dark:border-gray-600">
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-2">
                    <div class="h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                      <span class="text-white font-bold text-sm">{{ getTicketCode(selectedTicketForReponse.code) }}</span>
                    </div>
                    <div>
                      <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Ticket {{ selectedTicketForReponse.code }}
                      </h4>
                    </div>
                  </div>
                  
                  <!-- Sélecteur de statut compact -->
                  <div class="flex items-center space-x-2">
                    <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Statut:</span>
                    <select 
                      v-model="selectedTicketForReponse.status" 
                      @change="updateTicketStatus(selectedTicketForReponse)"
                      :class="[
                        'px-3 py-1 text-xs rounded-full font-medium border-0 focus:ring-2 focus:ring-purple-500 focus:outline-none transition-all duration-200 cursor-pointer min-w-[120px]',
                        getStatusBadgeClass(selectedTicketForReponse.status)
                      ]"
                      :disabled="isArchiveView"
                    >
                      <option value="ouvert" :class="getStatusOptionClass('ouvert')">Ouvert</option>
                      <option value="en_attente" :class="getStatusOptionClass('en_attente')">En attente</option>
                      <option value="resolu" :class="getStatusOptionClass('resolu')">Résolu</option>
                      <option value="ferme" :class="getStatusOptionClass('ferme')">Fermé</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Contenu informations compact avec scroll -->
              <div class="max-h-60 overflow-y-auto p-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  
                  <!-- Colonne Client -->
                  <div class="space-y-2">
                    <h5 class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center">
                      <svg class="h-3 w-3 text-purple-600 dark:text-purple-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                      Client
                    </h5>
                    <div class="space-y-1">
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Nom:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100 font-medium">{{ selectedTicketForReponse.nom }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Code Client:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100">{{ selectedTicketForReponse.codeclient || '-' }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Email:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100">{{ selectedTicketForReponse.email || '-' }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Téléphone:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100">{{ selectedTicketForReponse.telephone }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Wilaya:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100">{{ selectedTicketForReponse.wilaya || '-' }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Colonne Produit -->
                  <div class="space-y-2">
                    <h5 class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center">
                      <svg class="h-3 w-3 text-purple-600 dark:text-purple-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                      </svg>
                      Produit
                    </h5>
                    <div class="space-y-1">
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Gamme:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100 font-medium">{{ selectedTicketForReponse.gamme?.name }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Produit:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100">{{ selectedTicketForReponse.produit?.name || '-' }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Motif:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100">{{ selectedTicketForReponse.motif?.name }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">N° Lot:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100">{{ selectedTicketForReponse.numerolot || '-' }}</span>
                      </div>
                      <div class="flex justify-between">
                        <span class="text-xs text-gray-500 dark:text-gray-400">Date livraison:</span>
                        <span class="text-xs text-gray-900 dark:text-gray-100">{{ formatDate(selectedTicketForReponse.datelivraison) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Description compacte -->
                <div class="mt-4 space-y-2">
                  <h5 class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center">
                    <svg class="h-3 w-3 text-purple-600 dark:text-purple-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
                    </svg>
                    Description
                  </h5>
                  <div class="bg-gray-50 dark:bg-gray-600 rounded p-2 border border-gray-200 dark:border-gray-500">
                    <p class="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap max-h-20 overflow-y-auto">
                      {{ selectedTicketForReponse.description || 'Aucune description fournie' }}
                    </p>
                  </div>
                </div>

                <!-- Responsable compact -->
                <div class="mt-4 space-y-2">
                  <h5 class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide flex items-center">
                    <svg class="h-3 w-3 text-purple-600 dark:text-purple-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                    </svg>
                    Responsable
                  </h5>
                  <div class="flex items-center space-x-2 p-2 bg-gray-50 dark:bg-gray-600 rounded border border-gray-200 dark:border-gray-500">
                    <div v-if="selectedTicketForReponse.responsable" class="flex items-center space-x-2">
                      <div class="h-6 w-6 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                        <svg class="h-3 w-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                        </svg>
                      </div>
                      <div>
                        <div class="text-xs font-medium text-gray-900 dark:text-gray-100">
                          {{ selectedTicketForReponse.responsable.name }}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                          {{ selectedTicketForReponse.responsable.email }}
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-xs text-gray-500 dark:text-gray-400 italic">
                      Non assigné
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section Fichiers joints -->
            <div class="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
              <!-- En-tête compact -->
              <button @click="toggleFichiersDropdown"
                class="flex items-center justify-between w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 border-b border-gray-200 dark:border-gray-600">
                <div class="flex items-center space-x-2">
                  <div class="h-8 w-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                    <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Fichiers joints
                      <span v-if="selectedTicketForReponseFichiers.length > 0" class="text-gray-500">
                        ({{ selectedTicketForReponseFichiers.length }})
                      </span>
                    </h4>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <svg :class="[
                    'h-4 w-4 text-gray-500 transition-transform duration-200',
                    showFichiersList ? 'rotate-180' : ''
                  ]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </button>

              <!-- Liste des fichiers avec scroll -->
              <div v-if="showFichiersList" class="transition-all duration-200">
                <div v-if="loadingReponseFichiers" class="p-4 text-center">
                  <div class="inline-flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="text-sm">Chargement des fichiers...</span>
                  </div>
                </div>

                <div v-else-if="selectedTicketForReponseFichiers.length > 0" class="max-h-48 overflow-y-auto p-4 space-y-3">
                  <div v-for="(fichier, index) in selectedTicketForReponseFichiers" :key="fichier.chemin" 
                       :class="[
                         'p-3 transition-colors duration-150 rounded-lg border text-sm',
                         index % 2 === 0
                           ? 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                           : 'bg-gray-50 border-gray-100 dark:bg-gray-600/50 dark:border-gray-600',
                         'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                       ]">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center space-x-3 flex-1 min-w-0">
                        <span class="text-lg">{{ fichier.icone }}</span>
                        <div class="flex-1 min-w-0">
                          <div class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {{ fichier.nom }}
                          </div>
                          <div class="text-xs text-gray-500 dark:text-gray-400">
                            {{ fichier.taille }} • {{ fichier.extension.toUpperCase() }}
                          </div>
                        </div>
                      </div>
                      <div class="flex items-center space-x-2 ml-3">
                        <a :href="fichier.url" 
                           target="_blank" 
                           class="p-2 text-blue-600 dark:text-blue-400 hover:text-white hover:bg-blue-600 dark:hover:bg-blue-500 rounded-lg transition-all duration-200"
                           :title="`Voir ${fichier.nom}`"
                           @click.stop>
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                        </a>
                        <a :href="fichier.url" 
                           download 
                           class="p-2 text-green-600 dark:text-green-400 hover:text-white hover:bg-green-600 dark:hover:bg-green-500 rounded-lg transition-all duration-200"
                           :title="`Télécharger ${fichier.nom}`"
                           @click.stop>
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="p-6 text-center">
                  <div class="max-w-xs mx-auto">
                    <div class="h-12 w-12 bg-gray-100 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg class="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
                      </svg>
                    </div>
                    <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Aucun fichier joint</h5>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Aucun fichier n'a été joint à ce ticket
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section Réponses existantes - COMPACTE -->
            <div class="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
              <!-- En-tête compact -->
              <button @click="toggleReponsesDropdown"
                class="flex items-center justify-between w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 border-b border-gray-200 dark:border-gray-600">
                <div class="flex items-center space-x-2">
                  <div class="h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                    <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2v-1"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Réponses ({{ ticketReponses.length }})
                    </h4>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <svg :class="[
                    'h-4 w-4 text-gray-500 transition-transform duration-200',
                    showReponsesList ? 'rotate-180' : ''
                  ]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </button>

              <!-- Liste des réponses avec scroll -->
              <div v-if="showReponsesList" class="transition-all duration-200">
                <div v-if="loadingReponses" class="p-4 text-center">
                  <div class="inline-flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="text-sm">Chargement...</span>
                  </div>
                </div>

                <div v-else-if="ticketReponses.length > 0" class="max-h-48 overflow-y-auto p-4 space-y-3">
                  <div v-for="(reponse, index) in ticketReponses" :key="reponse.id" :class="[
                    'p-3 transition-colors duration-150 rounded-lg border text-sm',
                    index % 2 === 0
                      ? 'bg-white border-gray-200 dark:bg-gray-700 dark:border-gray-600'
                      : 'bg-gray-50 border-gray-100 dark:bg-gray-600/50 dark:border-gray-600',
                    'hover:bg-blue-50 dark:hover:bg-blue-900/20'
                  ]">
                    <div class="flex items-center justify-between mb-2">
                      <div class="flex items-center space-x-2">
                        <div class="h-6 w-6 bg-gradient-to-br from-blue-400 to-blue-500 rounded-full flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M4 5h16v16H4V5z" />
                            <path stroke-linecap="round" stroke-linejoin="round" d="M8 9h8M8 13h8M8 17h5" />
                          </svg>
                        </div>
                        <span class="text-xs text-gray-500 dark:text-gray-400">{{ formatReponseDate(reponse.date) }}</span>
                      </div>
                    </div>
                    <div class="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {{ reponse.reponse }}
                    </div>
                  </div>
                </div>

                <div v-else class="p-6 text-center">
                  <div class="max-w-xs mx-auto">
                    <div class="h-12 w-12 bg-gray-100 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg class="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                    </div>
                    <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Aucune réponse</h5>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Aucune réponse enregistrée
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section Tickets similaires -->
            <div class="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden" v-if="!isArchiveView">
              <!-- En-tête -->
              <div class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
                <div class="flex items-center space-x-2">
                  <div class="h-8 w-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                    <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Tickets Similaires
                    </h4>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Même gamme, produit et motif
                    </p>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="text-xs text-gray-500 dark:text-gray-400">
                    {{ similarTickets.length }} ticket(s) trouvé(s)
                  </span>
                </div>
              </div>

              <!-- Liste des tickets similaires -->
              <div class="max-h-60 overflow-y-auto p-4">
                <div v-if="loadingSimilarTickets" class="text-center py-4">
                  <div class="inline-flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                    <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="text-sm">Chargement des tickets similaires...</span>
                  </div>
                </div>

                <div v-else-if="similarTickets.length > 0" class="space-y-3">
                  <!-- En-tête de sélection -->
                  <div class="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-600 rounded-lg">
                    <div class="flex items-center space-x-3">
                      <input 
                        type="checkbox" 
                        v-model="selectAllSimilar"
                        @change="toggleSelectAllSimilar"
                        class="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      >
                      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Sélectionner tous les tickets similaires
                      </span>
                    </div>
                    <span class="text-xs text-gray-500 dark:text-gray-400">
                      {{ selectedSimilarTickets.length }} sélectionné(s)
                    </span>
                  </div>

                  <!-- Liste des tickets -->
                  <div v-for="ticket in similarTickets" :key="ticket.id" 
                       class="flex items-start space-x-3 p-3 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500 hover:shadow-sm transition-shadow duration-200">
                    <!-- Checkbox de sélection -->
                    <div class="flex-shrink-0 mt-1">
                      <input 
                        type="checkbox" 
                        :value="ticket.id"
                        v-model="selectedSimilarTickets"
                        class="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      >
                    </div>

                    <!-- Informations du ticket -->
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                          <div class="h-6 w-6 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span class="text-white font-bold text-xs">{{ getTicketCode(ticket.code) }}</span>
                          </div>
                          <span class="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {{ ticket.code }}
                          </span>
                          <span :class="[
                            'px-2 py-1 text-xs font-medium rounded-full',
                            getStatusBadgeClass(ticket.status)
                          ]">
                            {{ getStatusText(ticket.status) }}
                          </span>
                        </div>
                        <span class="text-xs text-gray-500 dark:text-gray-400">
                          {{ formatDate(ticket.created_at) }}
                        </span>
                      </div>

                      <!-- Client et informations -->
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                        <div>
                          <span class="text-gray-500 dark:text-gray-400">Client:</span>
                          <span class="text-gray-900 dark:text-gray-100 font-medium ml-1">{{ ticket.nom }}</span>
                        </div>
                        <div>
                          <span class="text-gray-500 dark:text-gray-400">Code Client:</span>
                          <span class="text-gray-900 dark:text-gray-100 ml-1">{{ ticket.codeclient || '-' }}</span>
                        </div>
                      </div>

                      <!-- Description avec toggle -->
                      <div class="mt-2">
                        <button 
                          @click="toggleTicketDescription(ticket.id)"
                          class="flex items-center space-x-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors duration-200"
                        >
                          <svg 
                            :class="[
                              'h-3 w-3 transition-transform duration-200',
                              expandedTicketDescriptions.includes(ticket.id) ? 'rotate-180' : ''
                            ]" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                          <span>Voir la description</span>
                        </button>

                        <div 
                          v-if="expandedTicketDescriptions.includes(ticket.id)"
                          class="mt-2 p-2 bg-gray-50 dark:bg-gray-500 rounded border border-gray-200 dark:border-gray-400"
                        >
                          <p class="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                            {{ ticket.description || 'Aucune description fournie' }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="text-center py-6">
                  <div class="max-w-xs mx-auto">
                    <div class="h-12 w-12 bg-gray-100 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg class="h-6 w-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
                      </svg>
                    </div>
                    <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Aucun ticket similaire</h5>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      Aucun autre ticket avec la même gamme, produit et motif
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section Ajouter une réponse -->
            <div class="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-gray-700 dark:to-gray-600 rounded-xl p-4 border border-purple-200 dark:border-purple-800" v-if="!isArchiveView">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center mb-3">
                <svg class="h-4 w-4 text-purple-600 dark:text-purple-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Nouvelle réponse
                <span v-if="selectedSimilarTickets.length > 0" class="ml-2 text-xs text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 px-2 py-1 rounded-full">
                  + {{ selectedSimilarTickets.length }} ticket(s) sélectionné(s)
                </span>
              </h4>

              <div class="space-y-2">
                <textarea v-model="reponseText" rows="4"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200 placeholder-gray-400"
                  placeholder="Saisissez votre réponse ici..."
                  :maxlength="maxReponseLength"></textarea>
                <div class="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>Visible par tous les utilisateurs</span>
                  <span>{{ reponseText.length }}/{{ maxReponseLength }}</span>
                </div>
              </div>

              <div class="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  <span v-if="selectedSimilarTickets.length > 0">
                    La réponse sera ajoutée à <strong>{{ selectedSimilarTickets.length + 1 }}</strong> ticket(s)
                  </span>
                  <span v-else>
                    La réponse sera ajoutée uniquement à ce ticket
                  </span>
                </div>

                <div class="flex justify-end space-x-2">
                  <button @click="closeReponseModal"
                    class="px-4 py-2 text-xs text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 font-medium">
                    Fermer
                  </button>
                  <button @click="saveReponse" :disabled="!reponseText.trim() || submittingReponse" :class="[
                    'px-4 py-2 text-xs text-white rounded-lg transition-all duration-200 font-semibold flex items-center space-x-1',
                    reponseText.trim() && !submittingReponse
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800'
                      : 'bg-purple-400 cursor-not-allowed'
                  ]">
                    <svg v-if="submittingReponse" class="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg"
                      fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                      </path>
                    </svg>
                    <svg v-else class="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>{{ submittingReponse ? '...' : 'Enregistrer' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de transfert -->
    <div v-if="showTransferModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm transition-all duration-300"
      @click.self="closeTransferModal">
      <div
        class="bg-white dark:bg-gray-800 shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-95 border border-gray-200 dark:border-gray-600">
            
        <!-- En-tête de la modal -->
        <div class="rounded-2xl bg-gradient-to-r from-blue-900 to-blue-700 duration-300 px-6 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-white">
                  Transférer le Ticket
                </h3>
                <p class="text-blue-100 text-sm">
                  {{ selectedTicketForTransfer?.code }} - {{ selectedTicketForTransfer?.nom }}
                </p>
              </div>
            </div>
            <button @click="closeTransferModal"
              class="h-8 w-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200 group">
              <svg class="h-5 w-5 text-white group-hover:scale-110 transition-transform" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Contenu de la modal -->
        <div class="p-6">
          <div v-if="selectedTicketForTransfer" class="space-y-6">
            
            <!-- Sélection du responsable -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Sélectionner un responsable
              </label>
              <select 
                v-model="selectedResponsable" 
                @change="onResponsableChange"
                class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Choisir un responsable...</option>
                <option 
                  v-for="responsable in responsablesList" 
                  :key="responsable.id" 
                  :value="responsable"
                >
                  {{ responsable.name }} ({{ responsable.email }})
                </option>
              </select>
            </div>

            <!-- Affichage des informations du responsable sélectionné -->
            <div v-if="selectedResponsable" class="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
              <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                <svg class="h-4 w-4 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
                Informations du responsable
              </h4>
              
              <!-- Dans la modal de transfert, section "Gammes assignées" -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Gammes assignées
                  <span class="text-gray-400 text-xs">
                    ({{ selectedResponsable.gammes?.length || 0 }} disponible(s))
                  </span>
                </label>
                <select 
                  v-model="selectedGamme"
                  @change="onGammeChange"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  :disabled="!selectedResponsable.gammes || selectedResponsable.gammes.length === 0"
                >
                  <option value="">Choisir une gamme...</option>
                  <option 
                    v-for="gamme in selectedResponsable.gammes" 
                    :key="gamme.id" 
                    :value="gamme"
                  >
                    {{ gamme.name }}
                  </option>
                </select>
              </div>

              <!-- Section "Produits assignés" - MODIFIÉE -->
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Produits assignés
                  <span class="text-gray-400 text-xs">
                    ({{ filteredProduits.length }} disponible(s) pour cette gamme)
                  </span>
                </label>
                <select 
                  v-model="selectedProduit"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  :disabled="!selectedGamme || filteredProduits.length === 0"
                >
                  <option value="">Choisir un produit...</option>
                  <option 
                    v-for="produit in filteredProduits" 
                    :key="produit.id" 
                    :value="produit"
                  >
                    {{ produit.name }}
                  </option>
                </select>
                
                <!-- Message d'information -->
                <div v-if="selectedGamme && filteredProduits.length === 0" 
                    class="text-xs text-yellow-600 dark:text-yellow-400 mt-2 flex items-center">
                  <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.998-.833-2.732 0L4.346 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                  Aucun produit assigné pour la gamme "{{ selectedGamme.name }}"
                </div>
                
                <div v-else-if="!selectedGamme" 
                    class="text-xs text-blue-600 dark:text-blue-400 mt-2 flex items-center">
                  <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Veuillez d'abord sélectionner une gamme
                </div>
              </div>

              <!-- Section Motifs assignés -->
              <div>
                <label class="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Motifs assignés
                  <span class="text-gray-400 text-xs">
                    ({{ selectedResponsable.motifs?.length || 0 }} disponible(s))
                  </span>
                </label>
                <select 
                  v-model="selectedMotif"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  :disabled="!selectedResponsable.motifs || selectedResponsable.motifs.length === 0"
                >
                  <option value="">Choisir un motif...</option>
                  <option 
                    v-for="motif in selectedResponsable.motifs" 
                    :key="motif.id" 
                    :value="motif"
                  >
                    {{ motif.name }}
                  </option>
                </select>
                <div v-if="selectedResponsable && (!selectedResponsable.motifs || selectedResponsable.motifs.length === 0)" 
                     class="text-xs text-red-500 dark:text-red-400 mt-2 flex items-center">
                  <svg class="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Aucun motif assigné à ce responsable
                </div>
              </div>
            </div>

            <!-- Informations actuelles du ticket -->
            <div class="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700">
              <h4 class="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center">
                <svg class="h-4 w-4 text-blue-900 dark:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                Informations actuelles du ticket
              </h4>
              <div class="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <span class="text-blue-600 dark:text-blue-400 text-xs font-medium">Gamme actuelle:</span>
                  <div class="font-medium text-blue-900 dark:text-blue-100 mt-1">
                    {{ selectedTicketForTransfer.gamme?.name }}
                  </div>
                </div>
                <div>
                  <span class="text-blue-600 dark:text-blue-400 text-xs font-medium">Produit actuel:</span>
                  <div class="font-medium text-blue-900 dark:text-blue-100 mt-1">
                    {{ selectedTicketForTransfer.produit?.name || 'Non spécifié' }}
                  </div>
                </div>
                <div>
                  <span class="text-blue-600 dark:text-blue-400 text-xs font-medium">Motif actuel:</span>
                  <div class="font-medium text-blue-900 dark:text-blue-100 mt-1">
                    {{ selectedTicketForTransfer.motif?.name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Boutons d'action -->
            <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button 
                @click="closeTransferModal"
                class="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200 font-medium"
              >
                Annuler
              </button>
              <button 
                @click="transferTicket"
                :disabled="!canTransfer"
                :class="[
                  'px-6 py-2 text-white rounded-lg transition-all duration-200 font-semibold flex items-center space-x-2',
                  canTransfer
                    ? 'bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                    : 'bg-gray-400 cursor-not-allowed'
                ]"
              >
                <svg v-if="transferring" class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                </svg>
                <span>{{ transferring ? 'Transfert...' : 'Transférer' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showAddTicketModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm transition-all duration-300"
      @click.self="closeAddTicketModal">
      <div
        class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-95 border border-gray-200 dark:border-gray-600">
        
        <!-- En-tête de la modal -->
        <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-3">
              <div class="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center">
                <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-white">
                  Ajouter un Nouveau Ticket
                </h3>
                <p class="text-green-100 text-sm">
                  Créer un nouveau ticket de réclamation
                </p>
              </div>
            </div>
            <button @click="closeAddTicketModal"
              class="h-8 w-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors duration-200 group">
              <svg class="h-5 w-5 text-white group-hover:scale-110 transition-transform" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Intégration du formulaire de réclamation -->
        <ReclamationFormm 
          v-if="showAddTicketModal"
          @ticket-created="handleTicketCreated"
          @cancel="closeAddTicketModal"
          :is-modal="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ReclamationFormm from './ReclamationFormm.vue';
import * as XLSX from 'xlsx';

export default {
  name: 'TicketsContent',
  components: {
    ReclamationFormm
  },
  props: {
    userPermissions: {
      type: Array,
      default: () => []
    },
    userRoles: {
      type: Array,
      default: () => []
    },
    searchQuery: {
      type: String,
      default: ''
    },
    isSearchActive: {
      type: Boolean,
      default: false
    },
    selectedTicketCode: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      tickets: [],
      loading: false,
      showDetailModal: false,
      selectedTicket: null,

      ticketCodeTimeout: null,
      lastProcessedTicketCode: '',

      showReponsesList: true,

      ticketHistorique: [],
      loadingHistorique: false,

      filteredByTicketCode: false,

      // Modal réponse
      showReponseModal: false,
      selectedTicketForReponse: null,
      ticketReponses: [],
      loadingReponses: false,
      reponseText: '',
      maxReponseLength: 1000,
      submittingReponse: false,
      nombreJoursRappel: null,
    dateRappelCalculee: null,
    definirRappelEnCours: false,
    showRappelsList: false,
    rappelsAujourdhui: [],
    loadingRappels: false,

      similarTickets: [],
      selectedSimilarTickets: [],
      selectAllSimilar: false,
      loadingSimilarTickets: false,
      expandedTicketDescriptions: [],
      showAddTicketModal: false,
      filteredProduits: [],
      // Filtres LOCAUX
      localSearchQuery: '',
      activeStatusFilter: 'all',
      statusFilters: [
        { value: 'all', label: 'Tous', color: 'bg-gray-100 text-gray-800 border-gray-300' },
        { value: 'ouvert', label: 'Ouverts', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
        { value: 'en_attente', label: 'En attente', color: 'bg-blue-100 text-blue-800 border-blue-300' },
        { value: 'resolu', label: 'Résolus', color: 'bg-green-100 text-green-800 border-green-300' },
        { value: 'ferme', label: 'Fermés', color: 'bg-gray-100 text-gray-800 border-gray-300' }
      ],

      // Modal transfert
      showTransferModal: false,
      selectedTicketForTransfer: null,
      responsablesList: [],
      selectedResponsable: null,
      selectedGamme: null,
      selectedProduit: null,
      selectedMotif: null,
      transferring: false,

      // Pagination
      currentPage: 1,
      itemsPerPage: 5,

      // Sélection des tickets
      selectedTickets: [],
      selectAllFiltered: false,

      // Ligne sélectionnée pour les boutons flottants
      selectedRow: null,
      selectedTicketForFloating: null,

      // Gestion du drag & click
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      dragStartTime: 0,
      dragThreshold: 5,
      clickPrevented: false,
      selectedTicketFichiers: [],
      selectedTicketForReponseFichiers: [],
      loadingFichiers: false,
      loadingReponseFichiers: false,

      // Position des boutons flottants
      floatingPosition: {
        top: 0,
        left: 0
      },

      initialLoadComplete: false,

      // État des dropdowns
      showFichiersList: true,

      // Archivage
      isArchiveView: false,
      archivageEnCours: false
    };
    
  },
  computed: {
    isAdmin() {
      const roles = this.userRoles || [];
      return roles.includes('Administration') ||
        roles.includes('admin') ||
        roles.includes('administrateur') ||
        roles.includes('Administrateur');
    },

    isResponsable() {
      const roles = this.userRoles || [];
      return roles.includes('responsable') ||
        roles.includes('Responsable');
    },
    isDelegue() {
      const roles = this.userRoles || [];
      return roles.includes('Délégué') ||
        roles.includes('délégué') ||
        roles.includes('delegue') ||
        roles.includes('Delegue');
    },
    canTransfer() {
      return this.selectedResponsable && 
            this.selectedGamme && 
            this.selectedProduit && 
            this.selectedMotif && 
            !this.transferring;
    },

    // Filtrage des tickets
    filteredTickets() {
      let filtered = this.tickets || [];

      const hasGlobalSearch = this.searchQuery && typeof this.searchQuery === 'string' && this.searchQuery.trim();
      const hasTicketCodeSearch = this.selectedTicketCode && typeof this.selectedTicketCode === 'string' && this.selectedTicketCode.trim();
      
      if (hasGlobalSearch) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(ticket => {
          const matches = 
            (ticket.nom && ticket.nom.toLowerCase().includes(query)) ||
            (ticket.email && ticket.email.toLowerCase().includes(query)) ||
            (ticket.telephone && ticket.telephone.toLowerCase().includes(query)) ||
            (ticket.numerolot && ticket.numerolot.toLowerCase().includes(query)) ||
            (ticket.code && ticket.code.toLowerCase().includes(query)) ||
            (ticket.codeclient && ticket.codeclient.toLowerCase().includes(query)) ||
            (ticket.gamme?.name && ticket.gamme.name.toLowerCase().includes(query)) ||
            (ticket.produit?.name && ticket.produit.name.toLowerCase().includes(query)) ||
            (ticket.motif?.name && ticket.motif.name.toLowerCase().includes(query)) ||
            (ticket.description && ticket.description.toLowerCase().includes(query)) ||
            (ticket.wilaya && ticket.wilaya.toLowerCase().includes(query)) ||
            (ticket.represprofert && ticket.represprofert.toLowerCase().includes(query));
          
          return matches;
        });
        
        this.filteredByTicketCode = false;
        
      } else if (hasTicketCodeSearch) {
        const searchCode = this.selectedTicketCode.toLowerCase().trim();
        filtered = filtered.filter(ticket => {
          const ticketCode = ticket.code ? ticket.code.toLowerCase().trim() : '';
          const matches = ticketCode.includes(searchCode) || ticketCode === searchCode;
          return matches;
        });
        
        this.filteredByTicketCode = true;
      } else {
        this.filteredByTicketCode = false;
      }

      if (!hasGlobalSearch && !this.filteredByTicketCode) {
        if (this.activeStatusFilter !== 'all') {
          filtered = filtered.filter(ticket => ticket.status === this.activeStatusFilter);
        }

        if (this.localSearchQuery && typeof this.localSearchQuery === 'string') {
          const localQuery = this.localSearchQuery.toLowerCase();
          filtered = filtered.filter(ticket =>
            (ticket.nom && ticket.nom.toLowerCase().includes(localQuery)) ||
            (ticket.email && ticket.email.toLowerCase().includes(localQuery)) ||
            (ticket.telephone && ticket.telephone.toLowerCase().includes(localQuery)) ||
            (ticket.numerolot && ticket.numerolot.toLowerCase().includes(localQuery)) ||
            (ticket.code && ticket.code.toLowerCase().includes(localQuery)) ||
            (ticket.codeclient && ticket.codeclient.toLowerCase().includes(localQuery)) ||
            (ticket.gamme?.name && ticket.gamme.name.toLowerCase().includes(localQuery)) ||
            (ticket.produit?.name && ticket.produit.name.toLowerCase().includes(localQuery)) ||
            (ticket.motif?.name && ticket.motif.name.toLowerCase().includes(localQuery)) ||
            (ticket.description && ticket.description.toLowerCase().includes(localQuery)) ||
            (ticket.wilaya && ticket.wilaya.toLowerCase().includes(localQuery)) ||
            (ticket.represprofert && ticket.represprofert.toLowerCase().includes(localQuery))
          );
        }
      }

      return filtered;
    },

    paginatedTickets() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredTickets.slice(start, end);
    },

    totalItems() {
      return this.filteredTickets.length;
    },

    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    
    formattedHistorique() {
      return this.ticketHistorique.map(item => {
        return {
          ...item,
          formattedDate: this.formatDate(item.date),
          statusClass: this.getStatusBadgeClass(item.action),
          statusText: this.getStatusText(item.action)
        };
      });
    },

    allFilteredTicketIds() {
      return this.filteredTickets.map(ticket => ticket.id);
    },
    
    visiblePages() {
      const current = this.currentPage;
      const total = this.totalPages;
      const range = [];
      
      for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
        range.push(i);
      }
      
      return range;
    },

    hasMorePages() {
      return this.totalPages > 5 && this.currentPage < this.totalPages - 2;
    }
  },
  watch: {
    selectedTicketCode: {
      handler(newCode, oldCode) {
        if (newCode && newCode.trim()) {
          if (this.initialLoadComplete) {
            this.handleTicketCodeFromProps(newCode);
          }
        } else {
          this.filteredByTicketCode = false;
        }
      },
      immediate: true,
      deep: true
    },

    nombreJoursRappel: {
    handler(newVal) {
      this.calculerDateRappel();
    },
    immediate: true
  },

    tickets: {
      handler(newTickets, oldTickets) {
        if (newTickets.length > 0 && !this.initialLoadComplete) {
          this.initialLoadComplete = true;
          
          if (this.selectedTicketCode && this.selectedTicketCode.trim()) {
            this.$nextTick(() => {
              this.applyTicketCodeFilter(this.selectedTicketCode);
            });
          }
        }
        
        if (this.selectedTicketCode && this.selectedTicketCode.trim() && newTickets.length > 0) {
          this.$nextTick(() => {
            this.applyTicketCodeFilter(this.selectedTicketCode);
          });
        }
      },
      immediate: true,
      deep: true
    },
     selectedGamme: {
    handler(newGamme, oldGamme) {
      if (newGamme && newGamme !== oldGamme) {
        // Filtrer les produits quand la gamme change
        this.filterProduitsByGamme();
        // Charger les produits depuis l'API si nécessaire
        this.fetchProduitsByGamme(newGamme.id);
      }
    },
    immediate: true,
    deep: true
  }
  },
  mounted() {
    this.fetchTickets();
    document.addEventListener('selectstart', this.preventTextSelection);
    this.setupClickOutsideListener();
    
    // Écouter le scroll pour mettre à jour la position des boutons
    if (this.$refs.tableContainer) {
      this.$refs.tableContainer.addEventListener('scroll', this.handleScroll);
      // Observer les changements de taille pour recalculer la position
      this.resizeObserver = new ResizeObserver(() => {
        this.updateFloatingButtonsPosition();
      });
      this.resizeObserver.observe(this.$refs.tableContainer);
    }

    this.fetchRappelsAujourdhui();
  
  // Optionnel: Rafraîchir toutes les 5 minutes
  setInterval(() => {
    this.fetchRappelsAujourdhui();
  }, 5 * 60 * 1000);
  },
  beforeUnmount() {
    document.removeEventListener('selectstart', this.preventTextSelection);
    document.removeEventListener('click', this.handleClickOutside);
    
    if (this.$refs.tableContainer) {
      this.$refs.tableContainer.removeEventListener('scroll', this.handleScroll);
    }
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    // Méthodes pour l'archivage
    async switchToArchives() {
      this.isArchiveView = true;
      this.loading = true;
      try {
        const response = await fetch('/api/tickets/archives', {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.tickets = data.tickets || [];
        } else {
          this.showError('Erreur lors du chargement des archives');
          this.tickets = [];
        }
      } catch (error) {
        this.showError('Erreur de connexion au serveur');
        this.tickets = [];
      } finally {
        this.loading = false;
      }
    },

    async switchToActiveTickets() {
      this.isArchiveView = false;
      await this.fetchTickets();
    },

    async archiverTicketsSelectionnes() {
      if (this.selectedTickets.length === 0) {
        this.showError('Veuillez sélectionner au moins un ticket à archiver');
        return;
      }

      if (!confirm(`Êtes-vous sûr de vouloir archiver ${this.selectedTickets.length} ticket(s) ?`)) {
        return;
      }

      try {
        const token = this.getCsrfToken();
        const promises = this.selectedTickets.map(ticketId =>
          fetch(`/api/tickets/${ticketId}/archiver`, {
            method: 'POST',
            headers: {
              'X-CSRF-TOKEN': token,
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'include'
          })
        );

        const responses = await Promise.all(promises);
        const allSuccessful = responses.every(response => response.ok);

        if (allSuccessful) {
          this.showSuccess(`${this.selectedTickets.length} ticket(s) archivé(s) avec succès`);
          this.selectedTickets = [];
          await this.fetchTickets();
        } else {
          this.showError('Erreur lors de l\'archivage de certains tickets');
        }
      } catch (error) {
        this.showError('Erreur lors de l\'archivage');
      }
    },

    async desarchiverTicket(ticket) {
      if (!confirm(`Êtes-vous sûr de vouloir désarchiver le ticket ${ticket.code} ?`)) {
        return;
      }

      try {
        const token = this.getCsrfToken();
        const response = await fetch(`/api/tickets/${ticket.id}/desarchiver`, {
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          this.showSuccess('Ticket désarchivé avec succès');
          if (this.isArchiveView) {
            await this.switchToArchives();
          } else {
            await this.fetchTickets();
          }
        } else {
          this.showError('Erreur lors du désarchivage du ticket');
        }
      } catch (error) {
        this.showError('Erreur lors du désarchivage');
      }
    },

    // async archivageAutomatique() {
    //   if (!confirm('Voulez-vous lancer l\'archivage automatique des tickets fermés depuis plus d\'un an ?')) {
    //     return;
    //   }

    //   this.archivageEnCours = true;
    //   try {
    //     const token = this.getCsrfToken();
    //     const response = await fetch('/api/tickets/archivage-automatique', {
    //       method: 'POST',
    //       headers: {
    //         'X-CSRF-TOKEN': token,
    //         'X-Requested-With': 'XMLHttpRequest'
    //       },
    //       credentials: 'include'
    //     });

    //     if (response.ok) {
    //       const data = await response.json();
    //       this.showSuccess(data.message);
    //       await this.fetchTickets();
    //     } else {
    //       this.showError('Erreur lors de l\'archivage automatique');
    //     }
    //   } catch (error) {
    //     this.showError('Erreur lors de l\'archivage automatique');
    //   } finally {
    //     this.archivageEnCours = false;
    //   }
    // },

    // Gestion des clics sur les lignes
    
    
    handleRowClick(event, ticketId) {
      // Si c'est un drag, on ignore le click
      if (this.clickPrevented || this.isDragging) {
        this.clickPrevented = false;
        return;
      }
      
      // Si on a cliqué directement sur les boutons flottants, on ignore
      if (event.target.closest('.floating-actions-modal')) {
        return;
      }
      
      // Vérification RENFORCÉE pour les éléments interactifs
      const interactiveSelectors = 'select, input, button, a, [role="button"], .btn, .action-button';
      if (event.target.matches(interactiveSelectors) || 
          event.target.closest(interactiveSelectors)) {
        return;
      }
      
      this.selectRow(ticketId);
    },

    // Sélection de ligne avec boutons flottants
    selectRow(ticketId) {
      if (this.selectedRow === ticketId) {
        // Désélectionner
        this.selectedRow = null;
        this.selectedTicketForFloating = null;
      } else {
        // Sélectionner
        this.selectedRow = ticketId;
        this.selectedTicketForFloating = this.tickets.find(t => t.id === ticketId);
        
        // Mettre à jour la position des boutons
        this.$nextTick(() => {
          this.updateFloatingButtonsPosition();
        });
      }
    },

    // Mise à jour de la position des boutons flottants
    updateFloatingButtonsPosition() {
      if (this.selectedRow && this.selectedTicketForFloating) {
        // Forcer la mise à jour en modifiant une propriété réactive
        this.$forceUpdate();
      }
    },

    // Style pour les boutons flottants
    getFloatingButtonsStyle() {
      if (!this.selectedRow || !this.$refs.tableContainer) return { display: 'none' };
      
      const container = this.$refs.tableContainer;
      const rowElement = container.querySelector(`[data-ticket-id="${this.selectedRow}"]`);
      
      if (!rowElement) return { display: 'none' };
      
      // Obtenir les positions relatives au conteneur de table
      const containerRect = container.getBoundingClientRect();
      const rowRect = rowElement.getBoundingClientRect();
      
      // Positionner au-dessus des colonnes "Sélection" et "Numéro"
      const firstCell = rowElement.querySelector('td:first-child');
      const secondCell = rowElement.querySelector('td:nth-child(2)');
      
      if (!firstCell || !secondCell) return { display: 'none' };
      
      const firstCellRect = firstCell.getBoundingClientRect();
      const secondCellRect = secondCell.getBoundingClientRect();
      
      // Calculer la position relative au conteneur de table
      const left = firstCellRect.left - containerRect.left + container.scrollLeft;
      const right = secondCellRect.right - containerRect.left + container.scrollLeft;
      const width = right - left;
      
      // Position verticale - au-dessus de la ligne
      const top = firstCellRect.top - containerRect.top + container.scrollTop - 45;
      
      return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        position: 'absolute',
        zIndex: '100'
      };
    },

    setupClickOutsideListener() {
      this.handleClickOutside = (e) => {
        // Ne ferme pas si on clique sur une modal ouverte
        if (this.showDetailModal || this.showReponseModal || this.showTransferModal) {
          return;
        }
        
        // Ferme seulement si on clique en dehors des lignes ET des boutons flottants
        const target = e.target;
        if (
          !target.closest('.table-row') && 
          !target.closest('.floating-actions-modal') &&
          !target.closest('.modal')
        ) {
          this.selectedRow = null;
          this.selectedTicketForFloating = null;
        }
      };
      document.addEventListener('click', this.handleClickOutside);
    },

    // Méthodes de drag améliorées
    startDrag(e) {
      // Si on clique sur un élément interactif, NE PAS démarrer le drag
      const interactiveElements = ['SELECT', 'INPUT', 'BUTTON', 'A'];
      const target = e.target;
      
      if (interactiveElements.includes(target.tagName) || 
          target.closest('select') || 
          target.closest('input') || 
          target.closest('button') || 
          target.closest('a')) {
        return;
      }

      const container = this.$refs.tableContainer;
      this.isDragging = false;
      this.clickPrevented = false;
      
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      
      this.startX = clientX;
      this.scrollLeft = container.scrollLeft;
      this.dragStartTime = Date.now();
      
      container.classList.add('dragging');
      e.preventDefault();
    },

    doDrag(e) {
      if (!this.startX) return;
      
      const container = this.$refs.tableContainer;
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      const deltaX = Math.abs(clientX - this.startX);
      
      // Si le dépassement du seuil, c'est un drag
      if (deltaX > this.dragThreshold) {
        this.isDragging = true;
        this.clickPrevented = true;
        
        const walk = (clientX - this.startX) * 1.5; // Réduit la vitesse de scroll
        container.scrollLeft = this.scrollLeft - walk;
        
        // Mettre à jour la position des boutons PENDANT le drag
        this.updateFloatingButtonsPosition();
      }
    },

    endDrag(e) {
      const container = this.$refs.tableContainer;
      
      // Réinitialiser après un court délai
      setTimeout(() => {
        this.isDragging = false;
        this.startX = 0;
        this.clickPrevented = false;
      }, 100);
      
      container.classList.remove('dragging');
    },

    preventTextSelection(e) {
      if (this.isDragging) {
        e.preventDefault();
      }
    },

    // Méthodes pour les modales
    async openReponseModal(ticket, event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      this.selectedTicketForReponse = ticket;
      this.reponseText = '';
      this.selectedSimilarTickets = [];
      this.selectAllSimilar = false;
      this.expandedTicketDescriptions = [];
      this.showReponsesList = true;
      this.showReponseModal = true;
      
      // Charger les données
      await Promise.all([
        this.fetchTicketReponses(ticket.id),
        this.fetchReponseTicketFichiers(ticket.id),
        this.fetchSimilarTickets(ticket.id)
      ]);
    },

    closeReponseModal(event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      this.showReponseModal = false;
      this.selectedTicketForReponse = null;
      this.ticketReponses = [];
      this.reponseText = '';
      this.submittingReponse = false;
      this.showReponsesList = true;
    },

    openTransferModal(ticket, event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      this.selectedTicketForTransfer = ticket;
      this.selectedResponsable = null;
      this.selectedGamme = null;
      this.selectedProduit = null;
      this.selectedMotif = null;
      this.showTransferModal = true;
      this.fetchResponsablesList();
    },

    closeTransferModal(event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      this.showTransferModal = false;
      this.selectedTicketForTransfer = null;
      this.selectedResponsable = null;
      this.selectedGamme = null;
      this.selectedProduit = null;
      this.selectedMotif = null;
      this.transferring = false;
    },

    viewTicket(ticket, event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      this.selectedTicket = ticket;
      this.showDetailModal = true;
      this.fetchTicketHistorique(ticket.id);
      this.fetchTicketFichiers(ticket.id);
    },

    closeDetailModal(event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      this.showDetailModal = false;
      this.selectedTicket = null;
      this.ticketHistorique = [];
    },

    // Toggle dropdowns
    toggleFichiersDropdown() {
      this.showFichiersList = !this.showFichiersList;
    },

    toggleReponsesDropdown() {
      this.showReponsesList = !this.showReponsesList;
    },

    // Méthodes principales
    async fetchTickets() {
      this.loading = true;
      try {
        const url = '/tickets';

        const response = await fetch(url, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.tickets = data.tickets || data.data || [];
          await this.fetchResponsablesForTickets();
        } else {
          const errorData = await response.text();
          this.showError('Erreur lors du chargement des tickets');
          this.tickets = [];
        }
      } catch (error) {
        this.showError('Erreur de connexion au serveur');
        this.tickets = [];
      } finally {
        this.loading = false;
      }
    },

    async fetchResponsablesForTickets() {
      const ticketsWithGammeMotif = this.tickets.filter(ticket => ticket.gamme && ticket.motif);
      
      for (let ticket of ticketsWithGammeMotif) {
        try {
          if (!ticket.responsable && ticket.gamme.id && ticket.motif.id) {
            const responsable = await this.findResponsable(ticket.gamme.id, ticket.motif.id);
            if (responsable) {
              // Utiliser Object.assign au lieu de this.$set
              Object.assign(ticket, { responsable: responsable });
            }
          }
        } catch (error) {
          console.error('Erreur lors du chargement du responsable:', error);
        }
      }
    },

    async findResponsable(gammeId, motifId) {
      try {
        const response = await fetch(`/tickets/responsable?gamme_id=${gammeId}&motif_id=${motifId}`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          return data.responsable || null;
        }
        return null;
      } catch (error) {
        console.error('Erreur lors de la recherche du responsable:', error);
        return null;
      }
    },

    handleTicketCodeFromProps(ticketCode) {
      if (ticketCode === this.lastProcessedTicketCode) {
        return;
      }
      
      if (this.ticketCodeTimeout) {
        clearTimeout(this.ticketCodeTimeout);
      }
      
      this.ticketCodeTimeout = setTimeout(() => {
        this.localSearchQuery = '';
        this.activeStatusFilter = 'all';
        this.currentPage = 1;
        this.filteredByTicketCode = true;
        this.lastProcessedTicketCode = ticketCode;
        
        if (this.tickets.length > 0) {
          this.applyTicketCodeFilter(ticketCode);
        }
      }, 150);
    },

    applyTicketCodeFilter(ticketCode) {
      this.$nextTick(() => {
        const filteredCount = this.filteredTickets.length;
        
        if (filteredCount === 1) {
          const ticket = this.filteredTickets[0];
          this.viewTicket(ticket);
        } else if (filteredCount === 0) {
          this.showError(`Aucun ticket trouvé avec le code: ${ticketCode}`);
        }
      });
    },

    toggleSelectAllFiltered() {
      if (this.selectAllFiltered) {
        this.selectedTickets = [...this.allFilteredTicketIds];
      } else {
        this.selectedTickets = [];
      }
    },

    applyStatusFilter(status) {
      this.activeStatusFilter = status;
      this.currentPage = 1;
    },

    getStatusBadgeClass(status) {
      const classes = {
        'ouvert': 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900 dark:text-yellow-300 dark:border-yellow-700',
        'en_attente': 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700',
        'resolu': 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-300 dark:border-green-700',
        'ferme': 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700'
      };
      return classes[status] || 'bg-gray-100 text-gray-800 border-gray-300';
    },

    getStatusText(status) {
      const texts = {
        'ouvert': 'Ouvert',
        'en_attente': 'En attente',
        'resolu': 'Résolu',
        'ferme': 'Fermé'
      };
      return texts[status] || status;
    },

    getTicketCode(id) {
      if (!id) return '??';
      return id.toString().padStart(3, '0').substring(0, 2);
    },

    getStatusOptionClass(status) {
      const classes = {
        'ouvert': 'bg-yellow-100 text-yellow-800',
        'en_attente': 'bg-blue-100 text-blue-800',
        'resolu': 'bg-green-100 text-green-800',
        'ferme': 'bg-gray-100 text-gray-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },

    async updateTicketStatus(ticket) {
      try {
        const token = this.getCsrfToken();
        const response = await fetch(`/api/tickets/${ticket.id}/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            status: ticket.status
          }),
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.showSuccess('Statut mis à jour avec succès');

          if (this.showDetailModal && this.selectedTicket) {
            await this.fetchTicketHistorique(this.selectedTicket.id);
          }

          this.fetchTickets();
        } else {
          const data = await response.json();
          this.showError(data.message || 'Erreur lors de la mise à jour du statut');
          this.fetchTickets();
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.showError('Erreur lors de la mise à jour du statut');
        this.fetchTickets();
      }
    },

    async fetchTicketHistorique(ticketId) {
      this.loadingHistorique = true;
      try {
        const response = await fetch(`/api/tickets/${ticketId}/historique`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.ticketHistorique = data.historique || [];
        } else {
          this.ticketHistorique = [];
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.ticketHistorique = [];
      } finally {
        this.loadingHistorique = false;
      }
    },

    async fetchTicketReponses(ticketId) {
      this.loadingReponses = true;
      try {
        const response = await fetch(`/api/tickets/${ticketId}/reponses`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.ticketReponses = data.reponses || [];
        } else {
          this.ticketReponses = [];
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.ticketReponses = [];
      } finally {
        this.loadingReponses = false;
      }
    },

    async saveReponse() {
      if (!this.reponseText.trim()) {
          this.showError('Veuillez saisir une réponse');
          return;
      }

      this.submittingReponse = true;
      try {
          const token = this.getCsrfToken();
          
          // ✅ CORRECTION : Utiliser l'ID du ticket au lieu de l'objet complet
          const ticketId = this.selectedTicketForReponse.id;
          
          const response = await fetch(`/api/tickets/${ticketId}/reponses`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-TOKEN': token,
                  'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                  reponse: this.reponseText.trim()
              }),
              credentials: 'include'
          });

          if (response.ok) {
              const data = await response.json();
              this.showSuccess('Réponse enregistrée avec succès');
              this.reponseText = '';
              await this.fetchTicketReponses(ticketId);
          } else {
              const data = await response.json();
              this.showError(data.message || 'Erreur lors de l\'enregistrement de la réponse');
          }
      } catch (error) {
          console.error('Erreur:', error);
          this.showError('Erreur lors de l\'enregistrement de la réponse');
      } finally {
          this.submittingReponse = false;
      }
    },

    async fetchResponsablesList() {
      try {
        const response = await fetch('/api/responsables-with-assignments', {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.responsablesList = data.responsables || [];
        } else {
          this.responsablesList = [];
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.responsablesList = [];
      }
    },

    // Calculer la date de rappel
  calculerDateRappel() {
    if (this.nombreJoursRappel && this.nombreJoursRappel > 0) {
      const date = new Date();
      date.setDate(date.getDate() + parseInt(this.nombreJoursRappel));
      this.dateRappelCalculee = date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } else {
      this.dateRappelCalculee = null;
    }
  },

  // Définir un rappel rapide
  definirRappelRapide(jours) {
    this.nombreJoursRappel = jours;
    this.definirRappel();
  },

  // Définir un rappel
  async definirRappel() {
    if (!this.selectedTicket || !this.nombreJoursRappel) {
      this.showError('Veuillez saisir un nombre de jours');
      return;
    }

    this.definirRappelEnCours = true;
    
    try {
      const token = this.getCsrfToken();
      const response = await fetch(`/api/tickets/${this.selectedTicket.id}/definir-rappel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': token,
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          nombre_jours: this.nombreJoursRappel
        }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.showSuccess('Rappel défini avec succès');
        
        // Mettre à jour le ticket
        this.selectedTicket.dateRappel = data.date_rappel;
        this.selectedTicket.dateRappel_formatee = data.ticket.dateRappel_formatee;
        
        // Réinitialiser
        this.nombreJoursRappel = null;
        this.dateRappelCalculee = null;
        
        // Mettre à jour la liste des tickets
        await this.fetchTickets();
        
        // Rafraîchir les rappels d'aujourd'hui
        await this.fetchRappelsAujourdhui();
        
      } else {
        const data = await response.json();
        this.showError(data.message || 'Erreur lors de la définition du rappel');
      }
    } catch (error) {
      console.error('Erreur:', error);
      this.showError('Erreur lors de la définition du rappel');
    } finally {
      this.definirRappelEnCours = false;
    }
  },

  // Supprimer un rappel
  async supprimerRappel() {
    if (!this.selectedTicket || !this.selectedTicket.dateRappel) {
      return;
    }

    if (!confirm('Êtes-vous sûr de vouloir supprimer ce rappel ?')) {
      return;
    }

    try {
      const token = this.getCsrfToken();
      const response = await fetch(`/api/tickets/${this.selectedTicket.id}/supprimer-rappel`, {
        method: 'POST',
        headers: {
          'X-CSRF-TOKEN': token,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.showSuccess('Rappel supprimé avec succès');
        
        // Mettre à jour le ticket
        this.selectedTicket.dateRappel = null;
        this.selectedTicket.dateRappel_formatee = null;
        
        // Mettre à jour la liste des tickets
        await this.fetchTickets();
        
        // Rafraîchir les rappels d'aujourd'hui
        await this.fetchRappelsAujourdhui();
        
      } else {
        const data = await response.json();
        this.showError(data.message || 'Erreur lors de la suppression du rappel');
      }
    } catch (error) {
      console.error('Erreur:', error);
      this.showError('Erreur lors de la suppression du rappel');
    }
  },

  // Récupérer les rappels d'aujourd'hui
  async fetchRappelsAujourdhui() {
    if (this.isArchiveView) return;
    
    this.loadingRappels = true;
    try {
      const response = await fetch('/api/tickets/rappels-aujourdhui', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.rappelsAujourdhui = data.tickets || [];
      } else {
        this.rappelsAujourdhui = [];
      }
    } catch (error) {
      console.error('Erreur:', error);
      this.rappelsAujourdhui = [];
    } finally {
      this.loadingRappels = false;
    }
  },

  // Toggle l'affichage des rappels
  toggleRappelsList() {
    this.showRappelsList = !this.showRappelsList;
    if (this.showRappelsList && this.rappelsAujourdhui.length === 0) {
      this.fetchRappelsAujourdhui();
    }
  },

  // Envoyer manuellement les rappels (pour admin)
  async envoyerRappelsManuellement() {
    if (!this.isAdmin) {
      this.showError('Seuls les administrateurs peuvent envoyer manuellement les rappels');
      return;
    }

    if (!confirm('Êtes-vous sûr de vouloir envoyer manuellement les rappels d\'aujourd\'hui ?')) {
      return;
    }

    try {
      const token = this.getCsrfToken();
      const response = await fetch('/api/tickets/envoyer-rappels', {
        method: 'POST',
        headers: {
          'X-CSRF-TOKEN': token,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.showSuccess(data.message || 'Rappels envoyés avec succès');
        await this.fetchRappelsAujourdhui();
      } else {
        const data = await response.json();
        this.showError(data.message || 'Erreur lors de l\'envoi des rappels');
      }
    } catch (error) {
      console.error('Erreur:', error);
      this.showError('Erreur lors de l\'envoi des rappels');
    }
  },

    onResponsableChange() {
  this.selectedGamme = null;
  this.selectedProduit = null;
  this.selectedMotif = null;
  this.filteredProduits = [];
  
  if (this.selectedResponsable) {
    // Sélectionner automatiquement la première gamme si disponible
    if (this.selectedResponsable.gammes && this.selectedResponsable.gammes.length > 0) {
      this.selectedGamme = this.selectedResponsable.gammes[0];
      // Charger IMMÉDIATEMENT les produits pour cette gamme
      this.$nextTick(() => {
        this.filterProduitsByGamme();
        // Si nécessaire, charger depuis l'API
        this.fetchProduitsByGamme(this.selectedGamme.id);
      });
    }
    
    // Sélectionner automatiquement le premier motif si disponible
    if (this.selectedResponsable.motifs && this.selectedResponsable.motifs.length > 0) {
      this.selectedMotif = this.selectedResponsable.motifs[0];
    }
  }
},
    onGammeChange() {
  this.selectedProduit = null;
  this.filterProduitsByGamme();
  
  // Si une gamme est sélectionnée, charger les produits correspondants
  if (this.selectedGamme) {
    this.fetchProduitsByGamme(this.selectedGamme.id);
  }
},

filterProduitsByGamme() {
  if (!this.selectedGamme || !this.selectedResponsable) {
    this.selectedProduit = null;
    this.filteredProduits = [];
    return;
  }
  
  // Filtrer d'abord à partir des produits du responsable
  if (this.selectedResponsable.produits && this.selectedResponsable.produits.length > 0) {
    // Essayer plusieurs façons de filtrer
    this.filteredProduits = this.selectedResponsable.produits.filter(produit => {
      // 1. Vérifier gamme_id direct
      if (produit.gamme_id && produit.gamme_id === this.selectedGamme.id) {
        return true;
      }
      // 2. Vérifier via l'objet gamme
      if (produit.gamme && produit.gamme.id === this.selectedGamme.id) {
        return true;
      }
      // 3. Vérifier via gamme_id dans les relations
      if (produit.relations && produit.relations.gamme_id === this.selectedGamme.id) {
        return true;
      }
      return false;
    });
    
    // Si aucun produit filtré, essayer de charger depuis l'API
    if (this.filteredProduits.length === 0) {
      console.log('Aucun produit filtré, chargement depuis API...');
      this.fetchProduitsByGamme(this.selectedGamme.id);
    } else {
      // Sélectionner automatiquement le premier produit si disponible
      if (this.filteredProduits.length > 0 && !this.selectedProduit) {
        this.selectedProduit = this.filteredProduits[0];
      }
    }
  } else {
    // Si le responsable n'a pas de produits, charger depuis l'API
    this.fetchProduitsByGamme(this.selectedGamme.id);
  }
},

    async transferTicket() {
      if (!this.canTransfer) return;

      this.transferring = true;
      try {
        const token = this.getCsrfToken();
        const response = await fetch(`/api/tickets/${this.selectedTicketForTransfer.id}/transfer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': token,
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            responsable_id: this.selectedResponsable.id,
            gamme_id: this.selectedGamme.id,
            produit_id: this.selectedProduit?.id || null,
            motif_id: this.selectedMotif.id
          }),
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.showSuccess('Ticket transféré avec succès');
          this.closeTransferModal();
          this.fetchTickets();
        } else {
          const data = await response.json();
          this.showError(data.message || 'Erreur lors du transfert du ticket');
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.showError('Erreur lors du transfert du ticket');
      } finally {
        this.transferring = false;
      }
    },
    async fetchProduitsByGamme(gammeId) {
  try {
    const response = await fetch(`/api/gammes/${gammeId}/produits`, {
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      const allProduits = data.data || [];
      
      // Mettre à jour filteredProduits avec les produits de la gamme
      this.filteredProduits = allProduits;
      
      // Sélectionner automatiquement le premier produit
      if (this.filteredProduits.length > 0 && !this.selectedProduit) {
        this.selectedProduit = this.filteredProduits[0];
      } else if (this.filteredProduits.length === 0) {
        this.selectedProduit = null;
      }
    } else {
      this.filteredProduits = [];
      this.selectedProduit = null;
    }
  } catch (error) {
    console.error('Erreur lors du chargement des produits:', error);
    this.filteredProduits = [];
    this.selectedProduit = null;
  }
},
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatReponseDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getCsrfToken() {
      const metaTag = document.querySelector('meta[name="csrf-token"]');
      if (metaTag) {
        return metaTag.getAttribute('content');
      }
      return '';
    },

    showSuccess(message) {
      alert('Succès: ' + message);
    },

    showError(message) {
      alert('Erreur: ' + message);
    },

    clearLocalSearch() {
      this.localSearchQuery = '';
    },

    clearTicketFilter() {
      this.$emit('clear-ticket-filter');
    },

    getHeaderTitle() {
      if (this.filteredByTicketCode) {
        return `Ticket ${this.selectedTicketCode}`;
      }
      return this.isArchiveView ? 'Archives des Tickets' : 'Gestion des Tickets';
    },

    getHeaderDescription() {
      if (this.filteredByTicketCode) {
        const count = this.filteredTickets.length;
        return `Affichage du ticket spécifique - ${count} résultat(s)`;
      }
      return this.isArchiveView ? 'Tickets archivés - Consultation seule' : 'Tickets assignés à votre domaine';
    },

    async exportSelectedTickets() {
      if (this.selectedTickets.length === 0) {
        this.showError('Veuillez sélectionner au moins un ticket à exporter');
        return;
      }

      try {
        const ticketsToExport = this.tickets.filter(ticket => 
          this.selectedTickets.includes(ticket.id)
        );

        const data = ticketsToExport.map(ticket => ({
          'Code': ticket.code,
          'Client': ticket.nom,
          'Code Client': ticket.codeclient || 'N/A',
          'Email': ticket.email,
          'Téléphone': ticket.telephone,
          'Gamme': ticket.gamme?.name,
          'Produit': ticket.produit?.name || 'Non spécifié',
          'Motif': ticket.motif?.name,
          'Statut': this.getStatusText(ticket.status),
          'Date Réclamation': this.formatDate(ticket.datereclamation),
          'Description': ticket.description
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(data);
        XLSX.utils.book_append_sheet(wb, ws, 'Tickets');

        const today = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `tickets_export_${today}.xlsx`);

        this.showSuccess('Export Excel généré avec succès');
      } catch (error) {
        this.showError('Erreur lors de l\'export');
      }
    },

    async deleteTicket(id) {
      if (confirm('Êtes-vous sûr de vouloir supprimer ce ticket ?')) {
        try {
          const token = this.getCsrfToken();
          const response = await fetch(`/api/tickets/${id}`, {
            method: 'DELETE',
            headers: {
              'X-CSRF-TOKEN': token,
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'include'
          });

          if (response.ok) {
            this.showSuccess('Ticket supprimé avec succès');
            this.fetchTickets();
          } else {
            const data = await response.json();
            this.showError(data.message || 'Erreur lors de la suppression');
          }
        } catch (error) {
          this.showError('Erreur lors de la suppression');
        }
      }
    },

    getGammeTransferName(ticket) {
      if (ticket.gammeTransferer && ticket.gammeTransferer.name) {
        return ticket.gammeTransferer.name;
      }
      if (ticket.gamme_id_transferer) {
        return this.getGammeName(ticket.gamme_id_transferer);
      }
      return 'Non spécifiée';
    },

    getProduitTransferName(ticket) {
      if (ticket.produitTransferer && ticket.produitTransferer.name) {
        return ticket.produitTransferer.name;
      }
      if (ticket.produit_id_transferer) {
        return this.getProduitName(ticket.produit_id_transferer);
      }
      return 'Non spécifié';
    },

    getMotifTransferName(ticket) {
      if (ticket.motifTransferer && ticket.motifTransferer.name) {
        return ticket.motifTransferer.name;
      }
      if (ticket.motif_id_transferer) {
        return this.getMotifName(ticket.motif_id_transferer);
      }
      return 'Non spécifié';
    },

    getGammeName(ticket) {
      if (ticket.gamme && ticket.gamme.name) {
        return ticket.gamme.name;
      }
      if (ticket.relations && ticket.relations.gamme) {
        return ticket.relations.gamme;
      }
      return 'Non spécifiée';
    },

    getProduitName(ticket) {
      if (ticket.produit && ticket.produit.name) {
        return ticket.produit.name;
      }
      if (ticket.relations && ticket.relations.produit) {
        return ticket.relations.produit;
      }
      return 'Non spécifié';
    },

    getMotifName(ticket) {
      if (ticket.motif && ticket.motif.name) {
        return ticket.motif.name;
      }
      if (ticket.relations && ticket.relations.motif) {
        return ticket.relations.motif;
      }
      return 'Non spécifié';
    },

    getGammeNameById(gammeId) {
      if (!gammeId) return 'Non spécifiée';
      const gamme = this.tickets.find(t => t.gamme?.id === gammeId)?.gamme;
      return gamme?.name || `Gamme #${gammeId}`;
    },

    getProduitNameById(produitId) {
      if (!produitId) return 'Non spécifié';
      const produit = this.tickets.find(t => t.produit?.id === produitId)?.produit;
      return produit?.name || `Produit #${produitId}`;
    },

    getMotifNameById(motifId) {
      if (!motifId) return 'Non spécifié';
      const motif = this.tickets.find(t => t.motif?.id === motifId)?.motif;
      return motif?.name || `Motif #${motifId}`;
    },

    async fetchTicketFichiers(ticketId) {
      this.loadingFichiers = true;
      try {
        const response = await fetch(`/api/tickets/${ticketId}/fichiers`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.selectedTicketFichiers = data.fichiers || [];
        } else {
          this.selectedTicketFichiers = [];
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.selectedTicketFichiers = [];
      } finally {
        this.loadingFichiers = false;
      }
    },

    // Récupérer les fichiers pour la modal de réponse
    async fetchReponseTicketFichiers(ticketId) {
      this.loadingReponseFichiers = true;
      try {
        const response = await fetch(`/api/tickets/${ticketId}/fichiers`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.selectedTicketForReponseFichiers = data.fichiers || [];
        } else {
          this.selectedTicketForReponseFichiers = [];
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.selectedTicketForReponseFichiers = [];
      } finally {
        this.loadingReponseFichiers = false;
      }
    },

    // Méthode pour charger les fichiers d'un ticket
    async loadTicketFichiers(ticket) {
      // Si les fichiers sont déjà chargés, on ne recharge pas
      if (ticket.fichiersLoaded) return;
      
      // Utiliser Object.assign au lieu de this.$set
      Object.assign(ticket, { loadingFichiers: true });
      
      try {
        const response = await fetch(`/api/tickets/${ticket.id}/fichiers`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          const fichiersAvecInfos = (data.fichiers || []).map(fichier => ({
            ...fichier,
            ...this.getFileInfo(fichier.chemin, fichier.nom)
          }));
          
          // Utiliser Object.assign au lieu de this.$set
          Object.assign(ticket, {
            fichiers: fichiersAvecInfos,
            fichiersLoaded: true,
            loadingFichiers: false
          });
        } else {
          // Utiliser Object.assign au lieu de this.$set
          Object.assign(ticket, {
            fichiers: [],
            fichiersLoaded: true,
            loadingFichiers: false
          });
        }
      } catch (error) {
        console.error('Erreur:', error);
        // Utiliser Object.assign au lieu de this.$set
        Object.assign(ticket, {
          fichiers: [],
          fichiersLoaded: true,
          loadingFichiers: false
        });
      }
    },

    // Méthode pour obtenir les informations du fichier
    getFileInfo(filePath, fileName = '') {
      const extension = filePath.split('.').pop().toLowerCase();
      const name = fileName || filePath.split('/').pop();
      
      // Déterminer l'icône en fonction de l'extension
      let icone = '📄';
      const iconMap = {
        'pdf': '📕',
        'doc': '📘',
        'docx': '📘',
        'xls': '📗',
        'xlsx': '📗',
        'jpg': '🖼️',
        'jpeg': '🖼️',
        'png': '🖼️',
        'gif': '🖼️',
        'zip': '📦',
        'rar': '📦'
      };
      icone = iconMap[extension] || '📄';
      
      // Taille fictive ou récupérée de l'API
      const taille = '1.2 MB'; // Vous pouvez adapter selon vos données
      
      return {
        nom: name,
        chemin: filePath,
        extension,
        icone,
        taille,
        url: filePath.startsWith('http') ? filePath : `/${filePath}`
      };
    },

    // Méthode pour charger les fichiers au survol de la ligne
    handleRowHover(ticket) {
      if (!ticket.fichiersLoaded && !ticket.loadingFichiers) {
        this.loadTicketFichiers(ticket);
      }
    },

    // Méthode pour tronquer le nom de fichier
    truncateFileName(fileName, maxLength = 20) {
      if (!fileName) return '';
      if (fileName.length <= maxLength) return fileName;
      
      // Garder l'extension du fichier
      const lastDotIndex = fileName.lastIndexOf('.');
      if (lastDotIndex === -1) {
        // Pas d'extension, tronquer simplement
        return fileName.substring(0, maxLength - 3) + '...';
      }
      
      const name = fileName.substring(0, lastDotIndex);
      const extension = fileName.substring(lastDotIndex);
      
      // Calculer la longueur maximale pour le nom (en laissant de la place pour l'extension et "...")
      const maxNameLength = maxLength - extension.length - 3;
      
      if (name.length <= maxNameLength) {
        return fileName;
      }
      
      return name.substring(0, maxNameLength) + '...' + extension;
    },

    // Méthodes pour l'affichage des données
    getDisplayGamme(ticket) {
      if (ticket.transferer && ticket.relations?.gammeTransferer) {
        return ticket.relations.gammeTransferer;
      }
      return ticket.relations?.gamme || ticket.gamme?.name || 'Non spécifiée';
    },

    getDisplayProduit(ticket) {
      if (ticket.transferer && ticket.relations?.produitTransferer) {
        return ticket.relations.produitTransferer;
      }
      return ticket.relations?.produit || ticket.produit?.name || 'Non spécifié';
    },

    getDisplayMotif(ticket) {
      if (ticket.transferer && ticket.relations?.motifTransferer) {
        return ticket.relations.motifTransferer;
      }
      return ticket.relations?.motif || ticket.motif?.name || 'Non spécifié';
    },

    getAncienneGamme(ticket) {
      if (ticket.transferer) {
        return ticket.relations?.gamme || ticket.gamme?.name || 'Non spécifiée';
      }
      return null;
    },

    getAncienProduit(ticket) {
      if (ticket.transferer) {
        return ticket.relations?.produit || ticket.produit?.name || 'Non spécifié';
      }
      return null;
    },

    getAncienMotif(ticket) {
      if (ticket.transferer) {
        return ticket.relations?.motif || ticket.motif?.name || 'Non spécifié';
      }
      return null;
    },

    handleScroll() {
      this.updateFloatingButtonsPosition();
    },

    async fetchSimilarTickets(ticketId) {
      this.loadingSimilarTickets = true;
      try {
        const response = await fetch(`/api/tickets/${ticketId}/similar`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          this.similarTickets = data.similar_tickets || [];
        } else {
          this.similarTickets = [];
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.similarTickets = [];
      } finally {
        this.loadingSimilarTickets = false;
      }
    },

    // Toggle sélection de tous les tickets similaires
    toggleSelectAllSimilar() {
      if (this.selectAllSimilar) {
        this.selectedSimilarTickets = this.similarTickets.map(ticket => ticket.id);
      } else {
        this.selectedSimilarTickets = [];
      }
    },

    // Toggle l'affichage de la description d'un ticket
    toggleTicketDescription(ticketId) {
      const index = this.expandedTicketDescriptions.indexOf(ticketId);
      if (index > -1) {
        this.expandedTicketDescriptions.splice(index, 1);
      } else {
        this.expandedTicketDescriptions.push(ticketId);
      }
    },

    // Sauvegarder la réponse (modifiée pour gérer plusieurs tickets)
    async saveReponse() {
      if (!this.reponseText.trim()) {
        this.showError('Veuillez saisir une réponse');
        return;
      }

      this.submittingReponse = true;
      try {
        const token = this.getCsrfToken();
        
        // Liste des tickets à mettre à jour (ticket principal + tickets sélectionnés)
        const ticketIds = [this.selectedTicketForReponse.id, ...this.selectedSimilarTickets];
        
        // Envoyer la réponse à tous les tickets sélectionnés
        const promises = ticketIds.map(ticketId => 
          fetch(`/api/tickets/${ticketId}/reponses`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': token,
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              reponse: this.reponseText.trim()
            }),
            credentials: 'include'
          })
        );

        // Attendre que toutes les requêtes soient terminées
        const responses = await Promise.all(promises);
        const allSuccessful = responses.every(response => response.ok);

        if (allSuccessful) {
          const totalTickets = ticketIds.length;
          this.showSuccess(`Réponse enregistrée avec succès sur ${totalTickets} ticket(s)`);
          this.reponseText = '';
          this.selectedSimilarTickets = [];
          this.selectAllSimilar = false;
          
          // Recharger les réponses du ticket principal
          await this.fetchTicketReponses(this.selectedTicketForReponse.id);
        } else {
          this.showError('Erreur lors de l\'enregistrement de certaines réponses');
        }
      } catch (error) {
        console.error('Erreur:', error);
        this.showError('Erreur lors de l\'enregistrement de la réponse');
      } finally {
        this.submittingReponse = false;
      }
    },
    // Dans votre composant Vue, remplacez la méthode deleteTicket par :
async archiverTicket(ticket) {
  if (confirm(`Êtes-vous sûr de vouloir archiver le ticket ${ticket.code} ?`)) {
    try {
      const token = this.getCsrfToken();
      const response = await fetch(`/api/tickets/${ticket.id}/archiver`, {
        method: 'POST',
        headers: {
          'X-CSRF-TOKEN': token,
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include'
      });

      if (response.ok) {
        this.showSuccess('Ticket archivé avec succès');
        
        // Recharger les tickets selon la vue actuelle
        if (this.isArchiveView) {
          await this.switchToArchives();
        } else {
          await this.fetchTickets();
        }
        
        // Réinitialiser la sélection
        if (this.selectedRow === ticket.id) {
          this.selectedRow = null;
          this.selectedTicketForFloating = null;
        }
      } else {
        const data = await response.json();
        this.showError(data.message || 'Erreur lors de l\'archivage du ticket');
      }
    } catch (error) {
      console.error('Erreur archivage:', error);
      this.showError('Erreur lors de l\'archivage du ticket');
    }
  }
},

openAddTicketModal() {
      this.showAddTicketModal = true;
    },

    // Fermer la modal d'ajout de ticket
    closeAddTicketModal() {
      this.showAddTicketModal = false;
    },

    // Gérer la création réussie d'un ticket
    handleTicketCreated(ticketData) {
      this.showAddTicketModal = false;
      this.showSuccess('Ticket créé avec succès!');
      // Recharger la liste des tickets
      this.fetchTickets();
    },
  }
};
</script>

<style scoped>
/* ===== STYLES GÉNÉRAUX ===== */
.space-y-6 > * + * {
  margin-top: 1.5rem;
}

/* ===== BOUTONS FLOTTANTS - CORRIGÉS POUR LE SCROLL ===== */
.floating-actions-modal {
  position: absolute;
  z-index: 100;
  pointer-events: auto;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(0, 0, 0, 0.1);
  animation: modalSlideIn 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform;
  min-height: 40px;
  /* S'assurer que les boutons suivent le scroll */
  transform: translateZ(0);
  backface-visibility: hidden;
}

.dark .floating-actions-modal {
  background: rgba(55, 65, 81, 0.95);
  border: 1px solid rgba(75, 85, 99, 0.8);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
}

@keyframes modalSlideIn {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Conteneur des boutons en ligne horizontale */
.floating-actions-modal > div {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
}

/* Styles pour les boutons dans la modal flottante */
.floating-actions-modal button {
  flex: 1;
  min-width: 40px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease-in-out;
  border: 1px solid transparent;
  font-size: 0;
}

.floating-actions-modal button:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

/* Couleurs spécifiques pour chaque bouton */
.floating-actions-modal button:nth-child(1) {
  color: #2563eb;
  border-color: #dbeafe;
}
.floating-actions-modal button:nth-child(1):hover {
  background-color: #2563eb;
  color: white;
}

.floating-actions-modal button:nth-child(2) {
  color: #2563eb;
  border-color: #dbeafe;
}
.floating-actions-modal button:nth-child(2):hover {
  background-color: #2563eb;
  color: white;
}

.floating-actions-modal button:nth-child(3) {
  color: #dc2626;
  border-color: #fecaca;
}
.floating-actions-modal button:nth-child(3):hover {
  background-color: #dc2626;
  color: white;
}

.floating-actions-modal button:nth-child(4) {
  color: #7c3aed;
  border-color: #e9d5ff;
}
.floating-actions-modal button:nth-child(4):hover {
  background-color: #7c3aed;
  color: white;
}

/* ===== STYLES DE LA TABLE ===== */
.table-container {
  position: relative;
  overflow-x: auto;
  overflow-y: auto;
  scroll-behavior: smooth;
  /* Important pour le positionnement des boutons flottants */
  transform: translateZ(0);
}

/* Style pour la ligne sélectionnée */
.selected-row {
  position: relative;
  background-color: #f0f9ff !important;
  border-left: 4px solid #3b82f6;
}

.dark .selected-row {
  background-color: rgba(59, 130, 246, 0.1) !important;
}

/* Assurer que chaque ligne a une position relative */
.table-row {
  position: relative;
}

/* Empêcher la sélection de texte pendant le drag */
.table-container.dragging {
  cursor: grabbing;
  user-select: none;
  -webkit-user-select: none;
  scroll-behavior: auto;
}

.table-container.dragging .table-row {
  cursor: grabbing;
}

/* Assurer que le tableau prend toute la largeur */
table {
  min-width: 100%;
  position: relative;
}

/* Style pour les cellules */
.px-4.py-4 {
  position: relative;
}

/* ===== STYLES RESPONSIVE ===== */
@media (max-width: 768px) {
  .floating-actions-modal {
    padding: 3px;
    min-height: 36px;
  }
  
  .floating-actions-modal button {
    min-width: 35px;
    height: 30px;
  }
  
  .floating-actions-modal button svg {
    width: 14px;
    height: 14px;
  }

  .floating-actions-modal > div {
    gap: 3px;
  }
}

@media (max-width: 640px) {
  .floating-actions-modal {
    padding: 2px;
    min-height: 32px;
  }
  
  .floating-actions-modal button {
    min-width: 32px;
    height: 28px;
  }
  
  .floating-actions-modal button svg {
    width: 12px;
    height: 12px;
  }
}

/* Empêcher le flash bleu sur les liens/clics pendant le drag */
.table-container.dragging * {
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
}

/* ===== STYLES POUR LE DRAG & DROP ===== */
.table-container {
  -webkit-overflow-scrolling: touch;
}

/* Améliorer les performances sur mobile */
.floating-actions-modal,
.table-container,
.table-row {
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000;
}

/* ===== STYLES POUR LES ÉTATS DE CHARGEMENT ===== */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.loading-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ===== STYLES POUR LES MODALES ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-height: 90vh;
  overflow-y: auto;
}

.dark .modal-content {
  background: #1f2937;
}

/* ===== STYLES POUR LES BADGES DE STATUT ===== */
.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid;
}

.status-ouvert {
  background-color: #fef3c7;
  color: #92400e;
  border-color: #f59e0b;
}

.status-en_attente {
  background-color: #dbeafe;
  color: #1e40af;
  border-color: #3b82f6;
}

.status-resolu {
  background-color: #d1fae5;
  color: #065f46;
  border-color: #10b981;
}

.status-ferme {
  background-color: #f3f4f6;
  color: #374151;
  border-color: #9ca3af;
}

/* Versions dark mode */
.dark .status-ouvert {
  background-color: #451a03;
  color: #fbbf24;
  border-color: #d97706;
}

.dark .status-en_attente {
  background-color: #1e3a8a;
  color: #60a5fa;
  border-color: #2563eb;
}

.dark .status-resolu {
  background-color: #064e3b;
  color: #34d399;
  border-color: #059669;
}

.dark .status-ferme {
  background-color: #374151;
  color: #d1d5db;
  border-color: #6b7280;
}

/* ===== STYLES POUR LA PAGINATION ===== */
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.dark .pagination-container {
  border-top-color: #4b5563;
  background-color: rgba(55, 65, 81, 0.5);
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
}

.dark .pagination-info {
  color: #d1d5db;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-button {
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  color: #6b7280;
  transition: all 0.2s;
}

.dark .pagination-button {
  border-color: #4b5563;
  color: #9ca3af;
}

.pagination-button:hover:not(:disabled) {
  background-color: #f3f4f6;
}

.dark .pagination-button:hover:not(:disabled) {
  background-color: #4b5563;
}

.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-page {
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.pagination-page.active {
  background-color: #1e40af;
  color: white;
}

.pagination-page:not(.active) {
  border: 1px solid #d1d5db;
  color: #374151;
}

.dark .pagination-page:not(.active) {
  border-color: #4b5563;
  color: #d1d5db;
}

/* ===== STYLES POUR LES FILTRES ===== */
.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-button {
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid;
  transition: all 0.2s;
}

.filter-button.active {
  transform: scale(1.05);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

/* ===== STYLES POUR LES EN-TÊTES DE TABLE ===== */
.table-header {
  background: linear-gradient(to right, #dbeafe, #eff6ff);
  border-bottom: 1px solid #e5e7eb;
}

.dark .table-header {
  background: linear-gradient(to right, #374151, #4b5563);
  border-bottom-color: #4b5563;
}

.table-header th {
  padding: 1rem 1.5rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.dark .table-header th {
  color: #d1d5db;
}

/* ===== STYLES POUR LES CELLULES DE TABLE ===== */
.table-cell {
  padding: 1rem 1.5rem;
  white-space: nowrap;
  border-bottom: 1px solid #f3f4f6;
}

.dark .table-cell {
  border-bottom-color: #374151;
}

/* ===== STYLES POUR LES ÉTATS VIDES ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.empty-state-icon {
  width: 5rem;
  height: 5rem;
  background-color: #dbeafe;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.dark .empty-state-icon {
  background-color: rgba(59, 130, 246, 0.1);
}

.empty-state-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.dark .empty-state-title {
  color: #d1d5db;
}

.empty-state-description {
  font-size: 0.875rem;
  color: #6b7280;
}

.dark .empty-state-description {
  color: #9ca3af;
}

/* ===== ANIMATIONS ===== */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    transform: translateY(-10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

.slide-in {
  animation: slideIn 0.3s ease-in-out;
}

/* ===== UTILITAIRES ===== */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Amélioration de la performance du scroll */
.table-container {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.table-container::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.table-container::-webkit-scrollbar-track {
  background: transparent;
}

.table-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.dark .table-container::-webkit-scrollbar-thumb {
  background: #4b5563;
}

.dark .table-container::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
</style>
